import { AIClient } from "@/ai/AIAssistant";
import {
  Address,
  Failure,
  isFailure,
  isOk,
  LocalizedText,
  ok,
  Ok,
  pending,
  Result,
  signer,
  Signer,
  SignerType,
  SpecialCircumstancesType,
} from "@/common/common";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  createEmailField,
  LocalDateFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";
import { ShareType, ShareTypeCode, sortByShareType } from "@/common/share";
import { AlertProps } from "@/components-v2/Alert";
import { DropdownMenuProps } from "@/components-v2/Dropdown";
import { SelectProps } from "@/components-v2/Select";
import { TagProps } from "@/components-v2/Tag";
import { ChangeArticlesOfAssociationModel } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import {
  addGeneralMeetingDocuments,
  addSubscriptionFormDocument,
  ArticlesOfAssociationChangeMode,
  CleanupFunction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  ensureShareTypeCodeInSharesForShareTypes,
  Evaluation,
  extractSectionWithMaxScoreFromArticles,
  FollowUpType,
  IncreaseOfShareCapitalSubscribersStatus,
  IncreaseOfShareCapitalSubscribersStatusForIssue,
  IncreaseOfShareCapitalSubscribersStatusForNominalValue,
  InputState,
  isLanguageChanged,
  NumberOfSharesForShareType,
  ProjectContext,
  removeSubscriptionFormDocument,
  ShareAllocation,
  ShareAllocationRound,
  ShareAllocationRoundsResponse,
  shareholdersWithShares,
  SharesForShareholder,
  total,
  totalPaidInPremium,
  totalPaidInShareCapital,
  updateArticlesModeModel,
  updateFollowUpStatusEvent,
} from "@/dbot/common/commonDomain";
import { CustomDocumentType } from "@/dbot/customDocument/customDocumentDomain";
import {
  findLinkedOptionExercise,
  LinkedOptionExercise,
} from "@/dbot/optionExercise/optionExerciseDomain";
import { noDialog } from "@/dbot/summary/summaryDomain";
import { showError, thunk } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TMessage, TMessageParam } from "@/i18n/tt";
import {
  NewShareClassController,
  ShareClassApi,
} from "@/shareholderRegister/transactions/shareClassDomain";
import { SuggestedCompany } from "@/types/CompanyInfo";
import {
  ActorsSelectorModel,
  loadActors,
  SelectedActor,
  shareRecipientActorTabs,
} from "@/types/SelectActors";
import { ShareholderDTO } from "@/types/Shareholder";
import { getAllCountriesSortedByName, makeCountrySelect, norway } from "@/utils/countries";
import { CURRENCY_NOK } from "@/utils/currency";
import { plusDays, today, toLocalDateISOString } from "@/utils/Dates";
import { percentOf, roundWithPrecision } from "@/utils/numbers";
import { nationalIdValidator } from "@/utils/other";
import { getFullName, validateName } from "@/utils/Roles";
import { isNotBlank } from "@/utils/strings";
import {
  resolveUrnReference,
  toShareholderReference,
  toSubscriberIdReference,
} from "@/utils/UrnReference";
import { newUuid } from "@/utils/uuid";
import { AxiosInstance } from "axios";
import Big from "big.js";
import {
  compact,
  debounce,
  defer,
  first,
  identity,
  isEqual,
  last,
  partition,
  sumBy,
  uniq,
  uniqBy,
} from "lodash";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import { ChangeEvent, ReactNode } from "react";
import { KindContributionMethod, KindContributionModel } from "./contributionInKindDomain";
import { DebtContributionModel } from "./debtContributionDomain";

export type IncreaseOfShareCapital = {
  type: DecisionType.increaseOfShareCapital;
  quickSetupType?: QuickSetupType;
  properties: ShareCapitalIncreaseType;
  isSubscriptionForm?: boolean;
  isOtherSigningForAllSubscribers?: boolean;
  signersForAllSubscribers?: SubscriberSigner[];
  periodForSubscription?: PeriodForSubscription;
  deadlineForSubscription?: string;
  isDisposableAccount?: boolean;
  isAccountGiven?: boolean;
  accountNumber?: string;
  usePowerOfAttorney?: boolean;
  powerOfAttorneyDate?: string;
  powerOfAttorneyText?: string;
  estimatedCosts?: number;
  specialCircumstancesUponSubscription?: SpecialCircumstancesType;
  specialCircumstancesUponSubscriptionTexts: LocalizedText[];
  specialCircumstancesAfterLastApprovedAnnualAccount?: SpecialCircumstancesType;
  specialCircumstancesAfterLastApprovedAnnualAccountTexts: LocalizedText[];
  contributionTypes: ContributionType[];
  updateArticlesAutomatically?: boolean;
  articleOfAssociationNumber?: number;
  articleChanges: ArticlesOfAssociationChangeMode[];
  updateCommitmentAutomatically?: boolean;
  includeSubscribersAsAttachment?: boolean;
};

export enum QuickSetupType {
  CASH_PREFERENTIAL_RIGHTS = "CASH_PREFERENTIAL_RIGHTS",
  CASH_PRIVATE_PLACEMENT = "CASH_PRIVATE_PLACEMENT",
}

export enum IncreaseOfShareCapitalType {
  shareIssue = "shareIssue",
  increaseNominalValue = "increaseNominalValue",
}

const increaseOfShareCapitalTypeCaption: { [key in IncreaseOfShareCapitalType]: TMessage } = {
  [IncreaseOfShareCapitalType.shareIssue]: {
    key: "decisions.increaseShareCapitalAgainstCash.sharesOrNominalValue.newShares",
  },
  [IncreaseOfShareCapitalType.increaseNominalValue]: {
    key: "decisions.increaseShareCapitalAgainstCash.sharesOrNominalValue.increaseNominalValue",
  },
};
export type ShareCapitalIncreaseType = ShareIssue | IncreaseNominalValue;

export type ShareIssue = {
  type: IncreaseOfShareCapitalType.shareIssue;
  nominalValue?: number;
  shareIssuePerShareTypes: ShareIssuePerShareType[];
  subscribers: SubscriberForIssue[];
  preferentialIssueSubscribers: SubscriberForIssue[];
  preferentialRight?: boolean;
  preferentialRightTransferable?: boolean;
  preferentialRightOversubscribe?: boolean;
  preferentialBoardRightsToSellSubscription?: boolean;
  externalSubscribers?: boolean;
  preferentialRightWaivedReason?: PreferentialRightWaivedType;
  preferentialRightWaivedReasonText?: string;
  preferentialRightWaivedReasonTexts?: LocalizedText[];
  shareholderRights?: ShareholderRights;
  isGuaranteeConsortiumEstablished?: boolean;
  guaranteeConsortiumCommission?: GuaranteeConsortiumCommission;
};

export type ShareIssuePerShareType = {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  issueMode: ShareIssueMode;
  sharePrice?: number;
  isNewShareType: boolean;
  isIndividualSharePrice?: boolean;
};

export type IncreaseNominalValue = {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  increaseInNominalValue?: number;
  sharePrice?: number;
  contributionPerShareTypes: Array<ContributionPerShareType>;
  subscribers?: Array<SubscriberForIncreaseNominalValue>;
};

export type SubscriberId = string;

export type SubscriberForIncreaseNominalValue = {
  subscriberid: SubscriberId;
  shareholderId: number;
  name?: string;
  address?: string;
  debtContribution?: DebtContribution;
  kindContribution?: KindContribution;
  isSigningSelf: boolean;
  signers: SubscriberSigner[];
};

export type ShareIssueMode = Fixed | Interval;

export type Fixed = {
  type: "fixed";
  numberOfShares?: number;
  calculateFromSubscriptions?: boolean;
};

export type Interval = { type: "interval"; from?: number; to?: number };

export type ContributionPerShareType = {
  contributionPerShare?: number;
  sharetypeId: number;
  name: string;
};

export type PeriodForSubscription = {
  from?: string;
  to?: string;
};

export type GuaranteeConsortiumCommission = {
  numberOfShares?: number;
  percentOfGuaranteedAmount?: number;
};

export enum PreferentialRightWaivedType {
  SHAREHOLDERS_IN_UNDERSTANDING = "SHAREHOLDERS_IN_UNDERSTANDING",
  DEEMED_NECESSARY = "DEEMED_NECESSARY",
  ADDED_EXPLANATION = "ADDED_EXPLANATION",
}

export enum ShareholderRights {
  FROM_REGISTRATION_DATE = "FROM_REGISTRATION_DATE",
  FROM_SUBSCRIPTION_DATE = "FROM_SUBSCRIPTION_DATE",
}

export function getNewNominalValue(nominalValue: number, increase: number) {
  return Big(nominalValue).plus(increase).toNumber();
}

export function isNominalValueValid(nominalValue: number) {
  const multiplier = 10000;
  return Big(nominalValue).times(multiplier).mod(1).toNumber() === 0;
}

export function isShareCapitalValid(numberOfShares: number, nominalValue: number) {
  const multiplier = 1000000000;
  const newShareCapital = Big(numberOfShares).times(nominalValue);
  return Big(newShareCapital).times(multiplier).mod(1).toNumber() === 0;
}

export enum ContributionType {
  cash = "cash",
  debt = "debt",
  kind = "kind",
}

export type DebtContribution = {
  type: ContributionType.debt;
  maxAmountToBeConverted?: number;
  totalAmountReceivable?: number;
  excessAmountMethod?: ExcessAmountMethod;
  shareTypePriorityOrder?: number[];
};

export type KindContribution = {
  type: ContributionType.kind;
  contributionMethod?: KindContributionMethod;
  value?: number;
  description?: LocalizedText[];
};

export function contributionTypeLabel(type: ContributionType): TMessage {
  switch (type) {
    case ContributionType.cash:
      return { key: `decisions.increaseShareCapitalAgainstCash.cash` };
    case ContributionType.debt:
      return { key: `decisions.increaseShareCapitalAgainstCash.debt` };
    case ContributionType.kind:
      return { key: `decisions.increaseShareCapitalAgainstCash.kind` };
  }
}

export enum ExcessAmountMethod {
  discharged = "discharged",
  remains_liability = "remains_liability",
  paidout = "paidout",
}

export const excessAmountMethodLabel: { [key in ExcessAmountMethod]: TMessage } = {
  [ExcessAmountMethod.discharged]: { key: "decisions.increaseShareCapitalAgainstCash.discharged" },
  [ExcessAmountMethod.remains_liability]: {
    key: "decisions.increaseShareCapitalAgainstCash.remains_liability",
  },
  [ExcessAmountMethod.paidout]: { key: "decisions.increaseShareCapitalAgainstCash.paidout" },
};

export function excessAmountMethodOptions(): ExcessAmountMethod[] {
  return Object.values(ExcessAmountMethod);
}

const specialCircumstancesCaptions: { [key in SpecialCircumstancesType]: TMessage } = {
  [SpecialCircumstancesType.NONE]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccount.noReason",
  },
  [SpecialCircumstancesType.NONE_UNKNOWN_TO_SUBSCRIBERS]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstances.noUnknownReason",
  },
  [SpecialCircumstancesType.ADDED_EXPLANATION]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstancesDescription",
  },
};

export type SubscribersDialogState = {
  leftPanel?: "settings" | "key-figures";
  actorsModel?: ActorsSelectorModel;
};

export class IncreaseOfShareCapitalForm {
  readonly type = DecisionType.increaseOfShareCapital;
  quickSetupType?: QuickSetupType;
  inputState?: InputState;
  shareIssue: ShareIssueModel;
  increaseNominalValue: IncreaseNominalValueModel;
  properties: ShareCapitalIncreaseTypeModel;
  isSubscriptionForm: BooleanFieldModel;
  isIncludeSubscribersAsAttachment: BooleanFieldModel;
  isOtherSigningForAllSubscribers: BooleanFieldModel;
  signersForAllSubscribers: SubscriberSigner[];
  periodForSubscription: PeriodForSubscriptionModel;
  deadlineForSubscription: LocalDateFieldModel;
  isDisposableAccount: BooleanFieldModel;
  isAccountGiven: BooleanFieldModel;
  accountNumber: StringFieldModel;
  usePowerOfAttorney: BooleanFieldModel;
  powerOfAttorneyDate: LocalDateFieldModel;
  powerOfAttorneyText: StringFieldModel;
  estimatedCosts: NumberFieldModel;
  specialCircumstancesUponSubscriptionModel: RadioFieldModel<SpecialCircumstancesType | undefined>;
  specialCircumstancesUponSubscriptionTexts: LocalizedStringFieldModel[];
  specialCircumstancesAfterLastApprovedAnnualAccountModel: RadioFieldModel<
    SpecialCircumstancesType | undefined
  >;
  increaseOfShareCapitalTypeModel: RadioFieldModel<IncreaseOfShareCapitalType | undefined>;
  articleOfAssociationNumber: ArticleSelectorModel;
  specialCircumstancesAfterLastApprovedAnnualAccountTexts: LocalizedStringFieldModel[];
  contributionTypes: OptionFieldModel<ContributionType>;
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  articleChangeModel: ChangeArticlesOfAssociationModel;
  updateCommitmentAutomatically: RadioFieldModel<boolean | undefined>;

  addSignerForAllSubscribersModel?: { nameField: StringFieldModel; emailField: StringFieldModel };
  selectedSubscriberDialogState: SubscriberDialogState;

  readonly decisionId: string;
  askToAddBoardStatement?: CustomDocumentType[];
  addBoardStatementResult?: Result<TMessage, TMessage>;

  subscibersDialogState?: SubscribersDialogState;
  subscriptionCompletionError?: AlertProps;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<IncreaseOfShareCapital>,
    public data?: IncreaseOfShareCapital,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false }, { autoBind: true });
    this.decisionId = context.decisionId;
    this.inputState = context.inputState;

    this.quickSetupType = data?.quickSetupType;

    this.shareIssue = new ShareIssueModel(
      this.dispatch,
      context,
      ExcessAmountMethod.discharged,
      this.updateRequiredDependencies,
      asShareIssue(this.data?.properties),
    );
    this.increaseNominalValue = new IncreaseNominalValueModel(
      context,
      ExcessAmountMethod.discharged,
      asIncreaseNominalValue(this.data?.properties),
    );
    this.properties =
      this.data?.properties.type === "increaseNominalValue"
        ? this.increaseNominalValue
        : this.shareIssue;
    this.isSubscriptionForm = new BooleanFieldModel({
      value: data?.isSubscriptionForm,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.isIncludeSubscribersAsAttachment = new BooleanFieldModel({
      value: data?.includeSubscribersAsAttachment ?? false,
      required: true,
    });
    this.isOtherSigningForAllSubscribers = new BooleanFieldModel({
      value: data?.isOtherSigningForAllSubscribers,
    });
    this.signersForAllSubscribers = data?.signersForAllSubscribers ?? [];
    this.periodForSubscription = new PeriodForSubscriptionModel(data?.periodForSubscription);
    this.deadlineForSubscription = new LocalDateFieldModel({
      value: data?.deadlineForSubscription,
      required: true,
    });
    this.isDisposableAccount = new BooleanFieldModel({
      value: data?.isDisposableAccount ?? true,
      required: true,
    });
    this.isAccountGiven = new BooleanFieldModel({
      value: data?.isAccountGiven ?? false,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.accountNumber = new StringFieldModel({ value: data?.accountNumber });
    this.usePowerOfAttorney = new BooleanFieldModel({
      value: data?.usePowerOfAttorney,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.powerOfAttorneyDate = new LocalDateFieldModel({ value: data?.powerOfAttorneyDate });
    this.powerOfAttorneyText = new StringFieldModel({ value: data?.powerOfAttorneyText });
    this.estimatedCosts = new NumberFieldModel({
      value: data?.estimatedCosts ?? 0,
      isPositiveOrZero: true,
      required: true,
    });
    this.specialCircumstancesUponSubscriptionTexts = createTextModels(
      data?.specialCircumstancesUponSubscriptionTexts,
    );
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts = createTextModels(
      data?.specialCircumstancesAfterLastApprovedAnnualAccountTexts,
    );
    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
    this.increaseOfShareCapitalTypeModel = new RadioFieldModel<
      IncreaseOfShareCapitalType | undefined
    >({
      value: data?.properties?.type,
      options: Object.values(IncreaseOfShareCapitalType),
      idFn: identity,
      labelFn: (value) => (value ? increaseOfShareCapitalTypeCaption[value] : { translated: "" }),
      onChange: (value) => {
        this.changeShareCapitalIncreaseType(value as IncreaseOfShareCapitalType);
        if (this.hasConflictingContributionTypes(value)) {
          this.contributionTypes.toggle(ContributionType.debt);
        }
      },
    });

    this.contributionTypes = new OptionFieldModel<ContributionType>({
      options: Object.values(ContributionType),
      value: Object.values(ContributionType).filter((e) => data?.contributionTypes?.includes(e)),
      idFn: (option) => option,
      labelFn: (option) => contributionTypeLabel(option),
      disabledFn: (option) => this.isContributionTypeDisabled(option),
      tooltipFn: (option) => {
        if (this.isContributionTypeDisabled(option)) {
          return option === ContributionType.debt
            ? { key: "decisions.increaseShareCapitalAgainstCash.selectIncreaseType.tooltip.debt" }
            : { key: "decisions.increaseShareCapitalAgainstCash.selectIncreaseType.tooltip.other" };
        }
      },
      required: true,
      onChange: (changed, selected) => {
        this.updateRequiredDependencies();
        if (
          changed.includes(ContributionType.cash) &&
          !selected.includes(ContributionType.cash) &&
          this.shareIssue.preferentialRight.value === true
        ) {
          this.shareIssue.preferentialRight.setValue(false);
        }
      },
    });
    this.specialCircumstancesUponSubscriptionModel = new RadioFieldModel<
      SpecialCircumstancesType | undefined
    >({
      value: data?.specialCircumstancesUponSubscription ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? specialCircumstancesCaptions[value] : { translated: "" }),
      onChange: this.updateRequiredDependencies,
    });
    this.specialCircumstancesAfterLastApprovedAnnualAccountModel = new RadioFieldModel<
      SpecialCircumstancesType | undefined
    >({
      value:
        data?.specialCircumstancesAfterLastApprovedAnnualAccount ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? specialCircumstancesCaptions[value] : { translated: "" }),
      onChange: this.updateRequiredDependencies,
    });
    this.updateArticlesAutomatically = updateArticlesModeModel(
      this.data?.updateArticlesAutomatically,
    );
    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );
    this.updateCommitmentAutomatically = new RadioFieldModel<boolean | undefined>({
      value: data?.updateCommitmentAutomatically,
      options: [false, true],
      idFn: (value) => (value === undefined ? "undefined" : String(value)),
      labelFn: (value) =>
        value === true
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.automatic",
            }
          : value === false
            ? {
                key: "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.manual",
              }
            : { translated: "" },
    });
    this.selectedSubscriberDialogState = noDialog();
    this.updateRequiredDependencies();
    //end IncreaseOfShareCapitalForm
  }

  hasConflictingContributionTypes(option: IncreaseOfShareCapitalType | undefined) {
    return (
      option === IncreaseOfShareCapitalType.increaseNominalValue &&
      this.contributionTypes.selected.length > 1 &&
      this.isDebtContributionActive
    );
  }

  isContributionTypeDisabled(option: ContributionType) {
    if (this.properties.type !== "increaseNominalValue") return false;

    const hasDebt = this.isDebtContributionActive;
    const hasCashOrKind = this.isKindContributionActive || this.containsCash;
    return (
      (hasDebt && option !== ContributionType.debt) ||
      (hasCashOrKind && option === ContributionType.debt)
    );
  }

  get isCapitalCallEnabled() {
    return this.context.featureToggles.committedCapital;
  }

  get currentNominalValue() {
    return this.inputState?.sharenominalValue || 0;
  }

  get currentNumberOfShares() {
    return total(this.inputState?.numberOfShares ?? []);
  }

  get currentShareCapital() {
    return Big(this.currentNumberOfShares).times(this.currentNominalValue).toNumber();
  }

  getIncreaseInNumberOfShares(bound: "minimum" | "maximum") {
    if (this.properties.type === IncreaseOfShareCapitalType.shareIssue) {
      return bound === "minimum"
        ? this.shareIssue.minNumberOfShares
        : this.shareIssue.maxNumberOfShares;
    }
    return 0;
  }

  get increaseInNominalValue() {
    if (this.properties.type === IncreaseOfShareCapitalType.increaseNominalValue) {
      return this.increaseNominalValue.increaseInNominalValue.toNumberOrZero();
    }
    return 0;
  }

  getIncreaseInShareCapital(bound: "minimum" | "maximum") {
    if (this.properties.type === IncreaseOfShareCapitalType.increaseNominalValue) {
      return Big(this.currentNumberOfShares)
        .times(this.increaseNominalValue.increaseInNominalValue.toNumberOrZero())
        .toNumber();
    }
    return Big(this.currentNominalValue).times(this.getIncreaseInNumberOfShares(bound)).toNumber();
  }

  getNewNumberOfShares(bound: "minimum" | "maximum") {
    return this.currentNumberOfShares + this.getIncreaseInNumberOfShares(bound);
  }

  get newNominalValue() {
    return Big(this.currentNominalValue).plus(this.increaseInNominalValue).toNumber();
  }

  getNewShareCapital(bound: "minimum" | "maximum") {
    return Big(this.currentShareCapital).plus(this.getIncreaseInShareCapital(bound)).toNumber();
  }

  get isShareCapitalIncreaseRange() {
    return this.getNewNumberOfShares("minimum") < this.getNewNumberOfShares("maximum");
  }

  get shareholdersWithShares() {
    return shareholdersWithShares(
      this.context.initialState.shareholders,
      this.inputState?.sharesForShareholders,
    );
  }

  get increaseInNumberOfShareholders() {
    if (this.properties.type === IncreaseOfShareCapitalType.shareIssue) {
      const shareholderIdsWithShares = new Set(this.shareholdersWithShares.map((s) => s.id));
      return this.activeSubscribersForIssue.filter(
        (s) => !s.shareholderId || !shareholderIdsWithShares.has(s.shareholderId),
      ).length;
    }
    return 0;
  }

  get numberOfShareholdersAfter() {
    return this.shareholdersWithShares.length + this.increaseInNumberOfShareholders;
  }

  get totalPaidInPremium() {
    return totalPaidInPremium(this.inputState?.numberOfShares);
  }

  get totalSharePremiumIncrease() {
    if (this.properties.type === IncreaseOfShareCapitalType.shareIssue) {
      return this.activeSubscribersForIssue
        .reduce((sum, subscriber) => sum.plus(subscriber.totalPremiumForSubscriber), Big(0))
        .toNumber();
    }
    return this.properties.totalPremium;
  }

  get newTotalPaidInPremium() {
    const totalPaidInPremium = this.totalPaidInPremium;
    return totalPaidInPremium
      ? Big(this.totalSharePremiumIncrease).plus(totalPaidInPremium).toNumber()
      : undefined;
  }

  get totalPaidInShareCapital() {
    return totalPaidInShareCapital(this.inputState?.numberOfShares);
  }

  get totalIncreaseInPaidInShareCapital() {
    return this.activeSubscribers
      .reduce((sum, subscriber) => sum.plus(subscriber.totalPriceForSubscriber), Big(0))
      .toNumber();
  }

  get newTotalPaidInShareCapital() {
    const totalPaidInShareCapital = this.totalPaidInShareCapital;
    return totalPaidInShareCapital
      ? Big(this.totalIncreaseInPaidInShareCapital).plus(totalPaidInShareCapital).toNumber()
      : undefined;
  }

  changeContributionTypes(
    _: ChangeEvent<HTMLInputElement>,
    option: ContributionType,
    checked: boolean,
  ) {
    const documentsToAdd: CustomDocumentType[] | undefined =
      option === ContributionType.kind
        ? ["boardStatementContributionInKind"]
        : option === ContributionType.debt
          ? ["boardStatementDebtConversion", "setOffDeclaration"]
          : undefined;

    if (documentsToAdd && checked) {
      const missingDocumentsToAdd = documentsToAdd.filter(
        (a) =>
          !this.context.currentDocuments.some(
            (d) =>
              d.documentData?.type === DocumentType.custom_document &&
              d.documentData.customDocumentType === a,
          ),
      );
      if (missingDocumentsToAdd.length > 0) {
        this.setAskToAddBoardStatement(missingDocumentsToAdd);
      }
    }
    this.contributionTypes.toggle(option);
  }

  addBoardStatementDocument() {
    this.addBoardStatementResult = pending();
    this.dispatch(
      this.askToAddBoardStatement?.map((d) => ({
        type: "dbot/add-documents",
        documentTypes: [DocumentType.custom_document],
        customDocumentType: d,
        onDone: (err) => [
          thunk(() => {
            this.addBoardStatementResult = undefined;
            if (!err) {
              this.setAskToAddBoardStatement();
            }
          }),
        ],
      })) ?? [],
    );
  }

  setAskToAddBoardStatement(customDocumentTypes?: CustomDocumentType[]) {
    this.askToAddBoardStatement = customDocumentTypes;
  }

  updateRequiredDependencies() {
    this.periodForSubscription.setRequired(this.isSubscriptionForm.value);
    this.accountNumber.setRequired(this.isAccountGiven.value && this.containsCash);
    this.powerOfAttorneyDate.setRequired(this.usePowerOfAttorney.value);
    this.powerOfAttorneyText.setRequired(this.usePowerOfAttorney.value);
    this.specialCircumstancesUponSubscriptionTexts.forEach((t) =>
      t.model.setRequired(
        this.specialCircumstancesUponSubscriptionModel.value ===
          SpecialCircumstancesType.ADDED_EXPLANATION,
      ),
    );
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.forEach((t) =>
      t.model.setRequired(
        this.specialCircumstancesAfterLastApprovedAnnualAccountModel.value ===
          SpecialCircumstancesType.ADDED_EXPLANATION,
      ),
    );

    // Validate for properties will just check either increaseNominalValue or shareIssue
    this.shareIssue.preferentialRightTransferable.setRequired(this.isPreferentialRight);
    this.shareIssue.preferentialRightOversubscribe.setRequired(this.isPreferentialRight);
    this.shareIssue.preferentialBoardRightsToSellSubscription.setRequired(this.isPreferentialRight);
    this.shareIssue.guaranteeConsortiumCommission.setRequired(
      this.shareIssue.isGuaranteeConsortiumEstablished.value && this.containsCash,
    );
    this.shareIssue.preferentialRightWaivedReasonModel.setRequired(
      !this.isPreferentialRight && this.containsCash,
    );
    this.shareIssue.nominalValue.setRequired(this.currentNumberOfShares === 0);
  }

  init(): CleanupFunction {
    if (this.data === undefined || this.data.articleOfAssociationNumber === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.inputState?.articles,
      });
    }
    const cleanupFns = [
      autorun(() => {
        const languages = uniq(this.context.currentDocuments.flatMap((d) => d.languages ?? []));
        if (
          isLanguageChanged(languages, this.specialCircumstancesUponSubscriptionTexts) ||
          isLanguageChanged(
            languages,
            this.specialCircumstancesAfterLastApprovedAnnualAccountTexts,
          ) ||
          isLanguageChanged(languages, this.shareIssue.preferentialRightWaivedReasonTexts)
        ) {
          runInAction(() => {
            this.specialCircumstancesUponSubscriptionTexts = updateTextModels(
              languages,
              this.specialCircumstancesUponSubscriptionTexts,
            );
            this.specialCircumstancesAfterLastApprovedAnnualAccountTexts = updateTextModels(
              languages,
              this.specialCircumstancesAfterLastApprovedAnnualAccountTexts,
            );
            this.shareIssue.preferentialRightWaivedReasonTexts = updateTextModels(
              languages,
              this.shareIssue.preferentialRightWaivedReasonTexts,
            );
          });
        }
      }),
      this.shareIssue.init(),
    ];
    return () => {
      cleanupFns.forEach((f) => {
        f();
      });
    };
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-input-state": {
        const inputState = this.context.inputState;
        if (!isEqual(this.inputState, inputState)) {
          this.inputState = inputState;
          this.shareIssue.onInputStateChange(inputState);
          this.increaseNominalValue.onInputStateChange(inputState);
          this.articleChangeModel.updateArticles(inputState?.articles);
        }
        break;
      }
      case "dbot/addIncreaseOfShareCapitalSubscribersForIssue": {
        this.shareIssue.setSubscribersModel(event.subscribers, this.isPreferentialRight);
        this.setSignersForAllSubscribers(event.signersForAllSubscribers);
        this.validateActiveSubscribers();
        break;
      }
      case "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue": {
        this.increaseNominalValue.setSubscriberModel(event.subscribers);
        this.setSignersForAllSubscribers(event.signersForAllSubscribers);
        this.validateActiveSubscribers();
        break;
      }
      case "dbot/explore-articles-of-association-success": {
        if (this.articleOfAssociationNumber.selected === undefined) {
          this.articleOfAssociationNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
      }
    }
  }

  setSignersForAllSubscribers(signers: SubscriberSigner[]) {
    if (signers.length > 0) {
      this.isSubscriptionForm.setValue(false);
      this.isOtherSigningForAllSubscribers.setValue(true);
      this.signersForAllSubscribers = this.signersForAllSubscribers.concat(signers);
    }
  }

  addSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = {
      nameField: new StringFieldModel({ required: true, validator: validateName }),
      emailField: new StringFieldModel({ required: true }),
    };
  }

  cancelSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = undefined;
  }

  submitSignerForAllSubscribers() {
    if (!this.addSignerForAllSubscribersModel) {
      return;
    }
    const { nameField, emailField } = this.addSignerForAllSubscribersModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.signersForAllSubscribers.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      this.addSignerForAllSubscribersModel = undefined;
    }
  }

  removeSignerForAllSubscribers(signer: SubscriberSigner) {
    this.signersForAllSubscribers.splice(this.signersForAllSubscribers.indexOf(signer), 1);
  }

  onCloseSelectedSubscriberDialog() {
    this.selectedSubscriber?.refreshSignerChoice();
    this.selectedSubscriberDialogState = noDialog();
  }

  get selectedSubscriber() {
    switch (this.selectedSubscriberDialogState.type) {
      case "none":
        return undefined;
      case "addDebtContribution":
      case "addContributionInKind":
      case "deleteSubscriber":
      case "addSigners": {
        const subscriberId = this.selectedSubscriberDialogState.subscriberId;
        return this.activeSubscribers.find((d) => d.subscriberid === subscriberId);
      }
    }
  }

  // Receives only the subscribers who are a part of the share capital increase
  get activeSubscribers(): SubscriberForCapitalIncrease[] {
    switch (this.properties.type) {
      case IncreaseOfShareCapitalType.shareIssue: {
        return this.activeSubscribersForIssue;
      }
      default:
        //Handles increase nominal value
        return this.properties.subscribers;
    }
  }

  // Receives only the subscribers who are a part of the issue and filtered by the selected share types
  get activeSubscribersForIssue(): SubscriberForIssueModel[] {
    const selectedShareTypeIds = this.shareIssue.selectedShareTypeIds;
    return this.subscribersForIssueCandidates.filter(
      (s) => s.getNumberOfSharesForShareTypes(selectedShareTypeIds) > 0,
    );
  }

  // Receives an unfiltered list of all subscribers added in the project
  get subscribersForIssueCandidates(): SubscriberForIssueModel[] {
    return this.isPreferentialRight
      ? this.shareIssue.preferentialIssueSubscribers
      : this.shareIssue.subscribers;
  }

  get isSubscriptionInProgress(): boolean {
    switch (this.properties.type) {
      case IncreaseOfShareCapitalType.shareIssue: {
        return this.subscribersForIssueCandidates.length > 0;
      }
      default: {
        return this.increaseNominalValue.isContributionPerShareFilled;
      }
    }
  }

  get calculatedSubscriptionProgress(): TagProps {
    switch (this.properties.type) {
      case IncreaseOfShareCapitalType.shareIssue: {
        const progress = getSubscriptionProgress(
          this.totalIssuedSharesForActiveSubscribers,
          this.shareIssue,
        );
        return toSubscriptionCompletionTag(progress);
      }
      default: {
        const isContributionPerShareTypesMissing =
          !this.increaseNominalValue.isContributionPerShareFilled;
        return {
          color: isContributionPerShareTypesMissing ? "neutral" : "success",
          label: {
            type: "percent",
            value: isContributionPerShareTypesMissing ? 0 : 1,
            maximumFractionDigits: 0,
          },
        };
      }
    }
  }

  get totalContributionForIssue() {
    const selectedShareTypeIds = this.shareIssue.selectedShareTypeIds;
    return this.activeSubscribersForIssue
      .reduce(
        (sum, subscriber) => sum.plus(subscriber.getTotalPriceForShareTypes(selectedShareTypeIds)),
        Big(0),
      )
      .toNumber();
  }

  get totalIssuedSharesForActiveSubscribers() {
    const selectedShareTypeIds = this.shareIssue.selectedShareTypeIds;
    return this.activeSubscribersForIssue.reduce(
      (sum, subscriber) => sum + subscriber.getNumberOfSharesForShareTypes(selectedShareTypeIds),
      0,
    );
  }

  calculateIssuedSharesForActiveSubscribers(shareType: { sharetypeId: number }) {
    return this.activeSubscribersForIssue.reduce(
      (sum, subscriber) => sum + subscriber.getNumberOfSharesForShareType(shareType.sharetypeId),
      0,
    );
  }

  isSharesIssuedForSelectedShareTypes() {
    return this.shareIssue.selectedShareTypes.selected.every(
      (shareType) =>
        this.calculateIssuedSharesForActiveSubscribers(shareType) <=
          shareType.model.maxNumberOfShares &&
        this.calculateIssuedSharesForActiveSubscribers(shareType) >=
          shareType.model.minNumberOfShares,
    );
  }

  get isActiveSubscribersValid(): boolean {
    const isActiveSubscribersValid =
      this.activeSubscribers.length > 0 &&
      (this.isLinkedToOptionExercise || !this.activeSubscribers.some((d) => !d.isValid));
    const isShareIssueValid =
      this.showShareIssue && this.isSharesIssuedForSelectedShareTypes() && isActiveSubscribersValid;
    const isIncreaseNominalValueValid = !this.showShareIssue && isActiveSubscribersValid;

    return isShareIssueValid || isIncreaseNominalValueValid;
  }

  openContributionInKindDialog(subscriber: SubscriberForCapitalIncrease) {
    this.selectedSubscriberDialogState = {
      type: "addContributionInKind",
      subscriberId: subscriber.subscriberid,
      model: new KindContributionModel(
        this.context.aiClient,
        this.context.documentLanguages,
        subscriber.totalPriceForSubscriber,
        this.currencyContributionInKinds,
        subscriber.kindContributionModel.toKindContribution(),
        !subscriber.address.isEmpty
          ? subscriber.address.validatedValue
          : subscriber.addressFromExistingShareholder,
        true,
      ),
    };
  }

  openDebtContributionDialog(subscriber: SubscriberForCapitalIncrease) {
    this.selectedSubscriberDialogState = {
      type: "addDebtContribution",
      subscriberId: subscriber.subscriberid,
      model: new DebtContributionModel(
        () => subscriber.totalPriceForSubscriber,
        () =>
          subscriber instanceof SubscriberForIssueModel
            ? subscriber.selectedSubscriptions
            : undefined,
        ExcessAmountMethod.discharged,
        subscriber.debtContributionModel.toDebtContribution(),
        !subscriber.address.isEmpty
          ? subscriber.address.validatedValue
          : subscriber.addressFromExistingShareholder,
        { required: true },
      ),
    };
  }

  get hasContributionInKind() {
    return this.subscribersWithContributionsInKind.length > 0;
  }

  get subscribersWithContributionsInKind() {
    return this.activeSubscribers.filter((subscriber) => subscriber.hasContributionInKind);
  }

  get currencyContributionInKinds() {
    return compact(
      this.subscribersWithContributionsInKind
        .filter((subscriber) => subscriber.hasCurrencyContributionInKind)
        .map((subscriber) => subscriber.kindContributionModel.toKindContribution()),
    );
  }

  get contributionInKindOptions() {
    return this.activeSubscribers.filter((subscriber) => !subscriber.hasContributionInKind);
  }

  get totalContributionInKind() {
    return this.subscribersWithContributionsInKind.reduce(
      (sum, subscriber) => sum + subscriber.kindContributionModel.value.toNumberOrZero(),
      0,
    );
  }

  get hasDebtContribution() {
    return this.subscribersWithDebtContributions.length > 0;
  }

  get isDebtContributionActive() {
    return this.contributionTypes.selected.includes(ContributionType.debt);
  }

  get isKindContributionActive() {
    return this.contributionTypes.selected.includes(ContributionType.kind);
  }

  get subscribersWithDebtContributions() {
    return this.activeSubscribers.filter((subscriber) => subscriber.hasDebtContribution);
  }

  get debtContributionOptions() {
    return this.activeSubscribers.filter((subscriber) => !subscriber.hasDebtContribution);
  }

  get totalDebtContribution() {
    return this.subscribersWithDebtContributions.reduce(
      (sum, subscriber) =>
        sum + subscriber.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
      0,
    );
  }

  setUsePowerOfAttorney(value: boolean) {
    this.usePowerOfAttorney.setValue(value);
    if (this.usePowerOfAttorney.value === false) {
      this.dispatch(addGeneralMeetingDocuments());
    } else {
      this.isSubscriptionForm.setValue(true);
      this.dispatch(addSubscriptionFormDocument());
    }
  }

  setIsSubscriptionForm(value: boolean) {
    this.isSubscriptionForm.setValue(value);
    if (this.isSubscriptionForm.value) {
      this.dispatch(addSubscriptionFormDocument());
    } else {
      this.dispatch(removeSubscriptionFormDocument());
    }
  }

  changeShareCapitalIncreaseType(value?: IncreaseOfShareCapitalType) {
    if (value === "shareIssue") {
      this.properties = this.shareIssue;
    } else {
      this.properties = this.increaseNominalValue;
    }
  }

  get showPowerOfAttorney() {
    return this.usePowerOfAttorney.value === true;
  }

  get showShareIssueOrIncreaseNominalValue() {
    return (
      this.usePowerOfAttorney.value === false ||
      (this.usePowerOfAttorney.value === true &&
        !this.powerOfAttorneyDate.isEmpty &&
        !this.powerOfAttorneyText.isEmpty)
    );
  }

  get hasChosenIncreaseMethod() {
    return this.contributionTypes.selected.length > 0;
  }

  get isShareIssue() {
    return this.properties.type === "shareIssue";
  }

  get showShareIssue() {
    return this.hasChosenIncreaseMethod && this.isShareIssue;
  }

  get showIncreaseNominalValue() {
    return this.hasChosenIncreaseMethod && this.properties.type === "increaseNominalValue";
  }

  get isPreferentialRight() {
    return this.containsCash && this.shareIssue.preferentialRight.value === true;
  }

  get optionExercise(): LinkedOptionExercise | undefined {
    return findLinkedOptionExercise(this.context.currentDecisions, this.context.decisionId);
  }

  get isLinkedToOptionExercise(): boolean {
    return this.optionExercise !== undefined;
  }

  openOptionExercise() {
    const decisionId = this.optionExercise?.decisionId;
    if (decisionId !== undefined) {
      this.dispatch({ type: "dbot/open-details-panel", panel: { type: "decision", decisionId } });
    }
  }

  get showSoleShareholderQuickAction(): boolean {
    return (
      !this.isLinkedToOptionExercise &&
      this.isShareIssue &&
      !this.isPreferentialRight &&
      this.activeSubscribers.length === 0 &&
      this.shareIssue.soleShareholder !== undefined
    );
  }

  get showPreferentialRightWaivedReason() {
    return this.shareIssue.preferentialRight.value === false;
  }

  get showIncreaseContributionPerShare() {
    return (
      this.isIncreaseNominalValueFilled &&
      this.isNewNominalValueValid &&
      this.isNewShareCapitalValid
    );
  }

  get isNewNominalValueValid() {
    const newNominalValue = getNewNominalValue(
      this.currentNominalValue,
      this.increaseNominalValue.increaseInNominalValue.toNumber() || 0,
    );
    return (
      !this.isIncreaseNominalValueFilled ||
      (this.increaseNominalValue.increaseInNominalValue.toNumberOrZero() > 0 &&
        isNominalValueValid(newNominalValue))
    );
  }

  get isIncreaseNominalValueFilled() {
    return this.increaseNominalValue.increaseInNominalValue.parsedValue !== undefined;
  }

  get isNewShareCapitalValid() {
    return isShareCapitalValid(
      this.currentNumberOfShares,
      getNewNominalValue(
        this.currentNominalValue,
        this.increaseNominalValue.increaseInNominalValue.toNumber() || 0,
      ),
    );
  }

  get showContributionInKind() {
    const shareIssueOk =
      !this.isShareIssue ||
      (this.showPreferentialRights && this.isPreferentialRight) ||
      this.showSubscribers;
    return shareIssueOk && this.contributionTypes.selected.includes(ContributionType.kind);
  }

  get showIncludeSubscribersAsAttachment() {
    return (
      this.isShareIssue &&
      (this.showSubscribers || (this.showPreferentialRights && this.isPreferentialRight))
    );
  }

  get showDebtConversion() {
    const shareIssueOk =
      !this.isShareIssue ||
      (this.showPreferentialRights && this.isPreferentialRight) ||
      this.showSubscribers;
    return shareIssueOk && this.contributionTypes.selected.includes(ContributionType.debt);
  }

  get hasSelectedShareTypes(): boolean {
    return (
      this.shareIssue.selectedShareTypes.selected.length > 0 &&
      !this.shareIssue.isSelectedShareTypesMissingValues
    );
  }

  get containsCash(): boolean {
    return this.contributionTypes.selected.includes(ContributionType.cash);
  }

  get isCashOnly(): boolean {
    return this.contributionTypes.selected.length === 1 && this.containsCash;
  }

  get showSubscribers(): boolean {
    return (
      this.showShareIssue &&
      this.hasSelectedShareTypes &&
      (!this.containsCash || this.shareIssue.preferentialRight.value === false)
    );
  }

  get showSubscriptionFormForShareIssue() {
    const isForCash =
      this.contributionTypes.isSelected(ContributionType.cash) &&
      this.shareIssue.preferentialRight.value === true &&
      this.shareIssue.preferentialRightTransferable.value !== undefined &&
      this.shareIssue.preferentialRightOversubscribe.value !== undefined &&
      this.shareIssue.preferentialBoardRightsToSellSubscription.value !== undefined;
    const isForDebt = this.contributionTypes.isSelected(ContributionType.debt);
    const isForKind = this.contributionTypes.isSelected(ContributionType.kind);
    return this.hasSelectedShareTypes && (isForCash || isForDebt || isForKind);
  }

  get showSubscriptionForm() {
    const noRightsIssue =
      this.shareIssue.preferentialRight.value === false &&
      this.shareIssue.preferentialRightWaivedReasonModel.value !== undefined;

    const forIncreaseNominalValue =
      this.properties.type === "increaseNominalValue" &&
      this.isIncreaseNominalValueFilled &&
      this.isNewNominalValueValid &&
      this.isNewShareCapitalValid &&
      this.increaseNominalValue.isContributionPerShareFilled;
    return this.showSubscriptionFormForShareIssue || noRightsIssue || forIncreaseNominalValue;
  }

  get showIsSubscribersSigning() {
    return this.isSubscriptionForm.value === false;
  }

  get showSignersForAllSubscribers() {
    return this.isOtherSigningForAllSubscribers.value === true;
  }

  get showDeadlineForSubscription() {
    return (
      this.isSubscriptionInMinutesValid ||
      (this.isSubscriptionForm.value === true &&
        !this.periodForSubscription.from.isEmpty &&
        !this.periodForSubscription.to.isEmpty)
    );
  }

  get deadlineForSubscriptionNotEmpty() {
    return !this.deadlineForSubscription.isEmpty;
  }

  get isUpdateArticlesAutomatically() {
    return this.updateArticlesAutomatically.value === true;
  }

  get isUpdateArticlesManually() {
    return this.updateArticlesAutomatically.value === false;
  }

  get showAccountInformation() {
    return this.containsCash;
  }

  get showShareholderRights() {
    // Shareholder rights question is displayed where there is:
    // - share issue
    // - the deadline for subscription is given
    return this.properties.type === IncreaseOfShareCapitalType.shareIssue;
  }

  get showGuaranteeConsortium(): boolean {
    return this.containsCash && this.showShareIssue;
  }

  get showShareIssueNominalValue() {
    return this.currentNumberOfShares === 0;
  }

  get showPreferentialRights() {
    return this.containsCash && this.hasSelectedShareTypes;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isArticleChangesValid(): boolean {
    const isAutomaticArticleChangesValid =
      this.isUpdateArticlesAutomatically && this.articleOfAssociationNumber.isValid;
    const isManualArticleChangesValid =
      this.isUpdateArticlesManually && this.articleChangeModel.isValid;
    return isAutomaticArticleChangesValid || isManualArticleChangesValid;
  }

  get isSignersForAllSubscribersEmpty(): boolean {
    return this.signersForAllSubscribers.length === 0;
  }

  get isSubscriptionInMinutesValid(): boolean {
    const isSubscribersSigningMinutes = this.isOtherSigningForAllSubscribers.value === false;
    const isOthersSigningMinutes =
      this.isOtherSigningForAllSubscribers.value === true && !this.isSignersForAllSubscribersEmpty;
    return (
      this.isSubscriptionForm.value === false &&
      (isSubscribersSigningMinutes || isOthersSigningMinutes)
    );
  }

  get isOtherSigningForAllSubscribersValid(): boolean {
    return this.isSubscriptionForm.value === true || this.isSubscriptionInMinutesValid;
  }

  get isValid() {
    return (
      this.contributionTypes.isValid &&
      this.properties.isValid &&
      this.isActiveSubscribersValid &&
      this.isSubscriptionForm.isValid &&
      this.isOtherSigningForAllSubscribersValid &&
      this.periodForSubscription.isValid &&
      this.deadlineForSubscription.isValid &&
      this.isDisposableAccount.isValid &&
      this.isAccountGiven.isValid &&
      this.accountNumber.isValid &&
      this.usePowerOfAttorney.isValid &&
      this.powerOfAttorneyDate.isValid &&
      this.powerOfAttorneyText.isValid &&
      this.estimatedCosts.isValid &&
      this.specialCircumstancesUponSubscriptionModel.value !== undefined &&
      this.specialCircumstancesUponSubscriptionTexts.every((t) => t.model.isValid) &&
      this.specialCircumstancesAfterLastApprovedAnnualAccountModel.value !== undefined &&
      this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.every((t) => t.model.isValid) &&
      this.updateArticlesAutomatically.isValid &&
      this.isArticleChangesValid
    );
  }

  validateActiveSubscribers() {
    this.activeSubscribers.forEach((d) => d.validate());
    this.validateSubscriptionCompletion();
  }

  clearSubscriptionCompletionError() {
    this.subscriptionCompletionError = undefined;
  }

  validateSubscriptionCompletion() {
    if (this.properties.type === IncreaseOfShareCapitalType.shareIssue) {
      const progress = getSubscriptionProgress(
        this.totalIssuedSharesForActiveSubscribers,
        this.shareIssue,
      );
      this.subscriptionCompletionError =
        progress.status !== "completed"
          ? {
              type: "error",
              message: {
                key: "decisions.increaseShareCapital.manageSubscribers.error",
                params: {
                  issued: { type: "number", value: progress.issued },
                  total: { type: "number", value: progress.total },
                },
              },
            }
          : undefined;
    }
  }

  validate(): boolean {
    this.contributionTypes.validate();
    this.properties.validate();
    this.validateActiveSubscribers();
    this.isSubscriptionForm.validate();
    this.periodForSubscription.validate();
    this.deadlineForSubscription.validate();
    this.isDisposableAccount.validate();
    this.isAccountGiven.validate();
    this.accountNumber.validate();
    this.usePowerOfAttorney.validate();
    this.powerOfAttorneyDate.validate();
    this.powerOfAttorneyText.validate();
    this.estimatedCosts.validate();
    this.specialCircumstancesUponSubscriptionModel.validate();
    this.specialCircumstancesUponSubscriptionTexts.forEach((t) => t.model.validate());
    this.specialCircumstancesAfterLastApprovedAnnualAccountModel.validate();
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.forEach((t) => t.model.validate());
    this.updateArticlesAutomatically.validate();
    this.articleOfAssociationNumber.validate();
    this.articleChangeModel.validate();
    return this.isValid;
  }

  get isQuickSetupApplied(): boolean {
    return this.quickSetupType !== undefined;
  }

  setQuickSetup(type?: QuickSetupType) {
    this.quickSetupType = type;
    this.dispatch({
      type: "show-alert",
      message: quickSetupSuccessMessage(type),
      messageType: "success",
    });
    if (type !== undefined) {
      this.autofillDecision();
    }
  }

  autofillDecision() {
    this.setUsePowerOfAttorney(false);
    this.contributionTypes.select(ContributionType.cash);
    this.changeShareCapitalIncreaseType(IncreaseOfShareCapitalType.shareIssue);
    if (this.shareIssue.selectedShareTypes.isNoneSelected) {
      if (this.shareIssue.selectedShareTypes.options.length > 0) {
        this.shareIssue.selectedShareTypes.toggle(this.shareIssue.selectedShareTypes.options[0]);
      } else {
        this.shareIssue.selectedShareTypes.toggleAll(true);
      }
      this.shareIssue.selectedShareTypes.selected.forEach((d) => {
        d.model.sharePrice.setAndCommitInputValue(this.currentNominalValue);
        d.model.changeIssueMode("fixed");
        const numberOfShares = Big(10000000).div(this.currentNominalValue).toNumber();
        d.model.fixed.numberOfShares.setAndCommitInputValue(numberOfShares);
      });
    }
    this.shareIssue.preferentialRight.setValue(
      this.quickSetupType === QuickSetupType.CASH_PREFERENTIAL_RIGHTS,
    );
    if (this.quickSetupType === QuickSetupType.CASH_PREFERENTIAL_RIGHTS) {
      this.shareIssue.preferentialRightTransferable.setValue(false);
      this.shareIssue.preferentialRightOversubscribe.setValue(false);
      this.shareIssue.preferentialBoardRightsToSellSubscription.setValue(false);
      this.shareIssue.externalSubscribers.setValue(false);
    } else {
      this.shareIssue.preferentialRightWaivedReasonModel.setValue(
        PreferentialRightWaivedType.SHAREHOLDERS_IN_UNDERSTANDING,
      );
    }
    this.shareIssue.setSubscribersForIssue();
    this.isSubscriptionForm.setValue(false);
    this.deadlineForSubscription.setAndCommitInputValue(
      toLocalDateISOString(plusDays(today(), 14)),
    );
    this.isDisposableAccount.setValue(true);
    this.updateArticlesAutomatically.setValue(true);
  }
}

export type SubscriptionProgress = {
  status: "in-progress" | "completed" | "exceeded";
  issued: number;
  total: number;
};

export function getSubscriptionProgress(
  issuedShares: number,
  {
    minNumberOfShares,
    maxNumberOfShares,
  }: {
    minNumberOfShares: number;
    maxNumberOfShares: number;
  },
): SubscriptionProgress {
  return {
    status:
      issuedShares === 0 || issuedShares < minNumberOfShares
        ? "in-progress"
        : issuedShares > maxNumberOfShares
          ? "exceeded"
          : "completed",
    issued: issuedShares,
    total: maxNumberOfShares,
  };
}

function toSubscriptionCompletionTag(progress: SubscriptionProgress): TagProps {
  const percent: TMessageParam = {
    type: "percent",
    value: percentOf(progress.issued, progress.total),
    maximumFractionDigits: 0,
  };
  switch (progress.status) {
    case "in-progress":
      return {
        color: "neutral",
        label: percent,
        tooltip: {
          key: "decisions.increaseShareCapital.manageSubscribers.status.in-progress",
          params: {
            issued: { type: "number", value: progress.issued },
            total: { type: "number", value: progress.total },
          },
        },
      };
    case "completed":
      return {
        color: "success",
        label: percent,
        tooltip:
          progress.issued < progress.total
            ? {
                key: "decisions.increaseShareCapital.manageSubscribers.status.in-progress",
                params: {
                  issued: { type: "number", value: progress.issued },
                  total: { type: "number", value: progress.total },
                },
              }
            : { key: "decisions.increaseShareCapital.manageSubscribers.status.completed" },
      };
    case "exceeded":
      return {
        color: "error",
        label: percent,
        tooltip: { key: "decisions.increaseShareCapital.manageSubscribers.status.exceeded" },
      };
  }
}

function createTextModels(data?: LocalizedText[]): LocalizedStringFieldModel[] {
  return (
    data?.map(
      ({ language, text }) =>
        new LocalizedStringFieldModel(new StringFieldModel({ value: text }), language),
    ) ?? []
  );
}

function updateTextModels(
  languages: Language[],
  models?: LocalizedStringFieldModel[],
): LocalizedStringFieldModel[] {
  return languages.map((lang) => {
    const localized = models?.find((t) => t.locale === lang);
    if (localized) {
      return localized;
    }
    return new LocalizedStringFieldModel(new StringFieldModel(), lang);
  });
}

function asShareIssue(data: ShareCapitalIncreaseType | undefined): ShareIssue | undefined {
  if (data?.type === "shareIssue") {
    return data;
  }
}

function asIncreaseNominalValue(
  data: ShareCapitalIncreaseType | undefined,
): IncreaseNominalValue | undefined {
  if (data?.type === "increaseNominalValue") {
    return data;
  }
}

export type ShareCapitalIncreaseTypeModel = ShareIssueModel | IncreaseNominalValueModel;

export class IncreaseNominalValueModel {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  increaseInNominalValue: NumberFieldModel;
  sharePrice: NumberFieldModel;
  contributionPerShareTypes: Array<ContributionPerShareTypeModel>;
  subscribers: SubscriberForIncreaseNominalValueModel[];
  inputState?: InputState;

  defaultExcessAmountMethod: ExcessAmountMethod;

  constructor(
    readonly context: DecisionContext<IncreaseOfShareCapital>,
    defaultExcessAmountMethod: ExcessAmountMethod,
    data: IncreaseNominalValue | undefined,
  ) {
    this.type = IncreaseOfShareCapitalType.increaseNominalValue;
    this.inputState = context.inputState;
    this.increaseInNominalValue = new NumberFieldModel({
      value: data?.increaseInNominalValue,
      required: true,
      isInteger: false,
      isPositive: true,
    });
    this.sharePrice = new NumberFieldModel({ value: data?.sharePrice, isPositiveOrZero: true });
    this.contributionPerShareTypes = this.createContributionPerShareTypeModel(
      data?.contributionPerShareTypes,
    );

    this.subscribers = this.toSubscriberForIncreaseModel(
      shareholdersWithShares(
        this.context.initialState.shareholders,
        this.inputState?.sharesForShareholders,
      ),
      data?.subscribers ?? [],
    );
    this.defaultExcessAmountMethod = defaultExcessAmountMethod;

    makeAutoObservable(this);
  }

  get isContributionPerShareFilled(): boolean {
    return !this.contributionPerShareTypes.some((c) => c.contributionPerShare.isEmpty);
  }

  get shareTypes(): ShareType[] {
    return sortByShareType(
      this.contributionPerShareTypes.map((x) => x.shareType),
      "shareTypeCode",
    );
  }

  private getShareType(shareTypeId: number) {
    return this.context.initialState.shareTypes.find((d) => d.sharetypeId === shareTypeId)!;
  }

  get numberOfSharesForShareType() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.context.initialState.shareTypes,
      this.inputState?.numberOfShares,
    );
  }

  get totalPremium(): number {
    return this.contributionPerShareTypes
      .reduce((acc, c) => {
        const premiumPerShare = Big(c.contributionPerShare.toNumberOrZero()).minus(
          this.increaseInNominalValue.toNumberOrZero(),
        );
        return acc.plus(premiumPerShare.times(c.numberOfShares));
      }, Big(0))
      .toNumber();
  }

  createContributionPerShareTypeModel(contributionPerShareTypes?: ContributionPerShareType[]) {
    return this.numberOfSharesForShareType
      .filter((d) => d.shareTypeCode !== undefined && d.numberOfShares > 0)
      .map((s) => {
        return {
          contributionPerShare:
            contributionPerShareTypes?.find((i) => i.sharetypeId === s.shareTypeId)
              ?.contributionPerShare ?? undefined,
          name: s.shareTypeCode!,
          sharetypeId: s.shareTypeId,
          shareType: this.getShareType(s.shareTypeId),
          numberOfShares: s.numberOfShares,
        };
      })
      .map(
        (s) =>
          new ContributionPerShareTypeModel(
            s,
            this.context,
            this.increaseInNominalValue,
            s.shareType,
            s.numberOfShares,
            { onChange: () => this.onContributionPerShareTypeChange() },
          ),
      );
  }

  onContributionPerShareTypeChange() {
    this.subscribers.forEach((s) => {
      s.onTotalPriceForSubscriberChange();
    });
  }

  onInputStateChange(inputState?: InputState) {
    const isNumberOfSharesChanged = !isEqual(
      this.inputState?.numberOfShares,
      inputState?.numberOfShares,
    );
    this.inputState = inputState;
    if (isNumberOfSharesChanged) {
      this.contributionPerShareTypes = this.createContributionPerShareTypeModel(
        this.contributionPerShareTypes.map((s) => s.toModel()),
      );
      this.onContributionPerShareTypeChange();
    }
  }

  setSubscriberModel(subscribers: SubscriberForIncreaseNominalValue[]) {
    this.subscribers = this.toSubscriberForIncreaseModel(
      shareholdersWithShares(
        this.context.initialState.shareholders,
        this.inputState?.sharesForShareholders,
      ),
      subscribers,
    );
  }

  toSubscriberForIncreaseModel(
    shareholders: ShareholderDTO[],
    data: SubscriberForIncreaseNominalValue[],
  ): SubscriberForIncreaseNominalValueModel[] {
    const subscribers = getSubscribersForIncreaseNominalValue(shareholders, data);
    if (!subscribers) {
      return [];
    }
    return subscribers.map(
      (d) =>
        new SubscriberForIncreaseNominalValueModel(
          d,
          this.context.initialState,
          this.inputState,
          () => this.contributionPerShareTypes.map((x) => x.toModel()),
          this.context.aiClient,
          this.context.documentLanguages,
          this.defaultExcessAmountMethod,
        ),
    );
  }

  get isValid(): boolean {
    return (
      this.increaseInNominalValue.isValid &&
      this.contributionPerShareTypes.every((contribution) => contribution.isValid)
    );
  }

  validate(): boolean {
    this.increaseInNominalValue.validate();
    this.contributionPerShareTypes.every((contribution) => contribution.validate());
    return this.isValid;
  }
}

export type ShareTypeOption = {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  shareType: ShareType;
  isNewShareType: boolean;
  model: ShareIssuePerShareTypeModel;
};

export type ExistingShareholder = {
  id: number;
  name: string;
  email?: string;
  isCompany: boolean;
  isSelected: boolean;
  contactName?: string;
  companyNumber?: string;
};

export type Subscription = {
  id: string;
  sharetypeId: number;
  shareTypeCode: ShareTypeCode;
  numberOfShares: number;
  subscriptionPrice: number;
};

export type SubscriberForIssue = {
  id: number;
  subscriberid: SubscriberId;
  shareholderId?: number;
  name?: string;
  email?: string;
  address?: string;
  nationality?: string;
  organisationNumber?: string;
  isExisting: boolean;
  isCompany: boolean;
  contactName?: string;
  debtContribution?: DebtContribution;
  kindContribution?: KindContribution;
  subscriptions: Subscription[];
  isSigningSelf: boolean;
  signers: SubscriberSigner[];
  nationalId?: string;
  personReference?: string;
};

export type SubscriberSigner = {
  id: string;
  name: string;
  email: string;
};

export type SubscriberForCapitalIncrease =
  | SubscriberForIncreaseNominalValueModel
  | SubscriberForIssueModel;

export enum SignerChoice {
  SELF = "SELF",
  OTHER = "OTHER",
  SELF_PLUS_OTHER = "SELF_PLUS_OTHER",
  TWO_OTHERS = "TWO_OTHERS",
}

const signerChoiceLabels: Record<SignerChoice, TMessage> = {
  [SignerChoice.SELF]: { key: "decisions.increaseShareCapital.signerChoice.self" },
  [SignerChoice.OTHER]: {
    key: "decisions.increaseShareCapital.signerChoice.other",
  },
  [SignerChoice.SELF_PLUS_OTHER]: {
    key: "decisions.increaseShareCapital.signerChoice.selfPlusOther",
  },
  [SignerChoice.TWO_OTHERS]: {
    key: "decisions.increaseShareCapital.signerChoice.twoOthers",
  },
};

export function signerChoiceLabel(choice: SignerChoice): TMessage {
  return signerChoiceLabels[choice];
}

export type SignerSelectOption =
  | { type: "choice"; choice: SignerChoice }
  | { type: "quickSigner"; name: string; email: string };

export function getUniqueOtherSigners(
  subscribers: SubscriberForCapitalIncrease[],
): { name: string; email: string }[] {
  return uniqBy(
    subscribers.flatMap((s) =>
      s.signers
        .filter((signer) => isNotBlank(signer.name.validatedValue))
        .map((signer) => ({
          name: signer.name.validatedValue,
          email: signer.email.validatedValue ?? "",
        })),
    ),
    (s) => s.name,
  ).slice(0, 3);
}

export function prepSignerSelectProps(
  subscriber: SubscriberForCapitalIncrease,
  subscribers: SubscriberForCapitalIncrease[],
): SelectProps<SignerSelectOption> {
  const quickSigners = getUniqueOtherSigners(subscribers);
  const items: SignerSelectOption[] = [
    { type: "choice", choice: SignerChoice.SELF },
    { type: "choice", choice: SignerChoice.OTHER },
    { type: "choice", choice: SignerChoice.SELF_PLUS_OTHER },
    { type: "choice", choice: SignerChoice.TWO_OTHERS },
    ...quickSigners.map((s) => ({ type: "quickSigner" as const, ...s })),
  ];
  return {
    value: items.find(
      (i) => i.type === "choice" && i.choice === subscriber.signerChoiceModel.value,
    ),
    items,
    idFn: (item) => (item.type === "choice" ? item.choice : `quick:${item.name}:${item.email}`),
    labelFn: (item) =>
      item.type === "choice"
        ? signerChoiceLabel(item.choice)
        : {
            key: "decisions.increaseShareCapital.signerChoice.applyToAll",
            params: { name: item.name },
          },
    sectionFn: (item) =>
      item === items.find((i) => i.type === "quickSigner")
        ? { key: "decisions.increaseShareCapital.signerChoice.quickActions" }
        : undefined,
    onSelect: (item) => {
      if (item.type === "quickSigner") {
        for (const sub of subscribers) {
          sub.signerChoiceModel.setValue(SignerChoice.OTHER);
          sub.signers[0]?.name.setAndCommitInputValue(item.name);
          sub.signers[0]?.email.setAndCommitInputValue(item.email);
        }
      } else {
        subscriber.signerChoiceModel.setValue(item.choice);
      }
    },
  };
}

function deriveSignerChoice(subscriber: SubscriberForCapitalIncrease): SignerChoice {
  switch (true) {
    case subscriber.isSigningSelf && subscriber.signers.length === 0:
      return SignerChoice.SELF;
    case subscriber.isSigningSelf && subscriber.signers.length > 0:
      return SignerChoice.SELF_PLUS_OTHER;
    case !subscriber.isSigningSelf && subscriber.signers.length <= 1:
      return SignerChoice.OTHER;
    default:
      return SignerChoice.TWO_OTHERS;
  }
}

function initSigners(m: SubscriberForCapitalIncrease) {
  switch (m.signerChoiceModel.value) {
    case SignerChoice.SELF:
      m.signers = [];
      break;
    case SignerChoice.SELF_PLUS_OTHER:
    case SignerChoice.OTHER:
      m.signers = m.signers.slice(0, 1);
      if (!m.signers.length) {
        m.addSigner();
      }
      break;
    case SignerChoice.TWO_OTHERS:
      m.signers = m.signers.slice(0, 2);
      while (m.signers.length < 2) {
        m.addSigner();
      }
      break;
  }
}

export function createSignerChoiceModel(
  m: SubscriberForCapitalIncrease,
): RadioFieldModel<SignerChoice> {
  return new RadioFieldModel<SignerChoice>({
    value: deriveSignerChoice(m),
    options: Object.values(SignerChoice),
    idFn: (c) => c,
    labelFn: signerChoiceLabel,
    onChange: (c) => {
      m.isSigningSelf = c === SignerChoice.SELF || c === SignerChoice.SELF_PLUS_OTHER;
      initSigners(m);
    },
  });
}

function createDebtContributionModel(
  subscriber: SubscriberForCapitalIncrease,
  defaultExcessAmountMethod: ExcessAmountMethod,
  data: DebtContribution | undefined,
) {
  return new DebtContributionModel(
    () => subscriber.totalPriceForSubscriber,
    () =>
      subscriber instanceof SubscriberForIssueModel ? subscriber.selectedSubscriptions : undefined,
    defaultExcessAmountMethod,
    data,
    !subscriber.address.isEmpty
      ? subscriber.address.validatedValue
      : subscriber.addressFromExistingShareholder,
  );
}

function createKindContributionModel(
  subscriber: SubscriberForCapitalIncrease,
  data: KindContribution | undefined,
) {
  return new KindContributionModel(
    subscriber.aiClient,
    subscriber.documentLanguages,
    () => subscriber.totalPriceForSubscriber,
    [],
    data,
  );
}

export class SubscriberForIncreaseNominalValueModel {
  subscriberid: SubscriberId;
  data: SubscriberForIncreaseNominalValue;
  shareholderId: number;
  nameField: StringFieldModel;
  isSigningSelf: boolean;
  signers: SubscriberSignerModel[];
  signerChoiceModel: RadioFieldModel<SignerChoice>;

  address: StringFieldModel;
  kindContributionModel: KindContributionModel;
  debtContributionModel: DebtContributionModel;

  defaultExcessAmountMethod: ExcessAmountMethod;

  constructor(
    data: SubscriberForIncreaseNominalValue,
    readonly initialState: Evaluation,
    readonly inputState: InputState | undefined,
    readonly contributionPerShareTypes: () => ContributionPerShareType[],
    readonly aiClient: AIClient,
    readonly documentLanguages: Language[],
    defaultExcessAmountMethod: ExcessAmountMethod,
  ) {
    this.subscriberid = data.subscriberid;
    this.data = data;
    this.shareholderId = data.shareholderId;
    this.nameField = new StringFieldModel({ value: data.name });
    this.isSigningSelf = data.isSigningSelf;
    this.signers = data.signers.map((s) => new SubscriberSignerModel(s));
    this.signerChoiceModel = createSignerChoiceModel(this);
    initSigners(this);

    this.address = new StringFieldModel({ value: data.address });
    this.kindContributionModel = createKindContributionModel(this, data?.kindContribution);
    this.debtContributionModel = createDebtContributionModel(
      this,
      defaultExcessAmountMethod,
      data?.debtContribution,
    );

    this.defaultExcessAmountMethod = defaultExcessAmountMethod;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get shareholder(): ShareholderDTO {
    return this.initialState.shareholders.find((s) => s.id === this.shareholderId)!;
  }

  refreshSignerChoice() {
    this.signerChoiceModel = createSignerChoiceModel(this);
  }

  onTotalPriceForSubscriberChange() {
    this.debtContributionModel.onTotalPriceForSubscriberChange();
  }

  get totalPriceForSubscriber(): number {
    return this.contributionPerShareTypes()
      .reduce((acc, contribution) => {
        const pricePerShareType = Big(
          this.getCurrentNumberOfSharesForShareType(contribution.sharetypeId),
        ).times(contribution.contributionPerShare ?? 0);

        return acc.plus(pricePerShareType);
      }, Big(0))
      .toNumber();
  }

  totalPriceForShareType(sharetypeId: number): number {
    const contribution = this.contributionPerShareTypes().find(
      (c) => c.sharetypeId === sharetypeId,
    );
    return Big(this.getCurrentNumberOfSharesForShareType(sharetypeId))
      .times(contribution?.contributionPerShare ?? 0)
      .toNumber();
  }

  getRemaining({
    isKindContributionActive,
    isDebtContributionActive,
  }: {
    isKindContributionActive: boolean;
    isDebtContributionActive: boolean;
  }): number {
    return Math.max(
      0,
      this.totalPriceForSubscriber -
        (isKindContributionActive ? this.kindContributionModel.value.toNumberOrZero() : 0) -
        (isDebtContributionActive
          ? this.debtContributionModel.maxAmountToBeConverted.toNumberOrZero()
          : 0),
    );
  }

  get hasContributionInKind() {
    return !this.kindContributionModel.isEmpty;
  }

  get hasCurrencyContributionInKind() {
    return this.kindContributionModel.isCurrencyContribution;
  }

  setContributionInKind(data?: KindContribution, address?: string) {
    this.kindContributionModel = createKindContributionModel(this, data);
    if (address !== undefined) {
      this.address.setAndCommitInputValue(address);
    }
  }

  get hasDebtContribution() {
    return !this.debtContributionModel.isEmpty;
  }

  setDebtContribution(data?: DebtContribution, address?: string) {
    this.debtContributionModel = createDebtContributionModel(
      this,
      this.defaultExcessAmountMethod,
      data,
    );
    if (address !== undefined) {
      this.address.setAndCommitInputValue(address);
    }
  }

  get email(): string {
    return (
      this.initialState.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.email || ""
    );
  }

  get addressFromExistingShareholder(): string {
    return getAddressAsString(
      this.initialState.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.address,
    );
  }

  get isCompany(): boolean {
    return (
      this.initialState.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.isCompany || false
    );
  }

  get ssnOrCompanyNumber(): string | undefined {
    const shareholder = this.initialState.shareholders.find((s) => s.id === this.shareholderId);
    const value = shareholder?.isCompany
      ? shareholder?.companyNumber
      : shareholder?.personInfo?.socialSecurityNumber;
    return value !== "" ? value : undefined;
  }

  get sharesForShareholder() {
    return (
      this.inputState?.sharesForShareholders.filter(
        (shareholder) => shareholder.shareholderId === this.shareholderId,
      ) ?? []
    );
  }

  getCurrentNumberOfSharesForShareType(shareTypeId: number): number {
    return (
      this.sharesForShareholder.find((shareholder) => shareholder.shareTypeId === shareTypeId)
        ?.numberOfShares ?? 0
    );
  }

  get isSignerChoiceSelf() {
    return this.signerChoiceModel.value === SignerChoice.SELF;
  }

  addSigner(name: string = "", email: string = "") {
    this.signers.push(new SubscriberSignerModel({ id: newUuid(), name: name, email: email }));
  }

  removeSigner(signer: SubscriberSignerModel) {
    if (this.signers.includes(signer)) {
      this.signers.splice(this.signers.indexOf(signer), 1);
    }
  }

  get isValid(): boolean {
    return !this.nameField.isEmpty;
  }

  validate(): boolean {
    this.nameField.validate();
    return this.isValid;
  }

  toSigner(): Signer {
    return signer({
      id: this.shareholderId,
      reference: toSubscriberIdReference(this.subscriberid),
      name: this.isCompany
        ? getFullName(this.shareholder.contactInfo)
        : this.nameField.validatedValue,
      email: this.email,
      signerType: SignerType.SUBSCRIPTION,
    });
  }

  get allSigners() {
    return compact([
      this.isSigningSelf &&
        new SubscriberSignerModel(
          {
            ...this.toSigner(),
            id: this.subscriberid,
          },
          true,
        ),
      ...this.signers,
    ]);
  }
}

export class SubscriberForIssueModel {
  subscriberid: SubscriberId;
  nameField: StringFieldModel;
  email: StringFieldModel;
  organisationNumber: StringFieldModel;
  contactName: StringFieldModel;
  data: SubscriberForIssue;
  shareholderId?: number;
  isExisting: boolean;
  isCompany: boolean;
  subscriptions: SubscriptionForIssueModel[];
  signers: SubscriberSignerModel[];
  isSigningSelf: boolean;
  signerChoiceModel: RadioFieldModel<SignerChoice>;
  address: StringFieldModel;
  nationality: StringFieldModel;
  nationalId: StringFieldModel;
  kindContributionModel: KindContributionModel;
  debtContributionModel: DebtContributionModel;
  defaultExcessAmountMethod: ExcessAmountMethod;

  constructor(
    data: SubscriberForIssue,
    readonly initialState: Evaluation,
    readonly inputState: InputState | undefined,
    readonly selectedShareTypeIds: () => number[],
    readonly aiClient: AIClient,
    readonly documentLanguages: Language[],
    defaultExcessAmountMethod: ExcessAmountMethod,
    readonly shareNominalValue: () => number,
  ) {
    this.subscriberid = data.subscriberid;
    this.shareholderId = data.shareholderId;
    this.nameField = new StringFieldModel({
      value: data.isExisting ? this.existingShareholderData?.name : data.name,
    });
    this.email = createEmailField({
      value: data.isExisting ? this.emailFromExistingShareholder : data.email,
    });
    this.organisationNumber = new StringFieldModel({
      value: data.isExisting ? this.ssnOrCompanyNumber : data.organisationNumber,
    });
    this.contactName = new StringFieldModel({
      value: data.isExisting
        ? getFullName(this.existingShareholderData?.contactInfo)
        : data.contactName,
    });
    this.data = data;
    this.isExisting = data.isExisting;
    this.isCompany = data.isCompany;
    this.subscriptions = data.subscriptions.map(
      (d) =>
        new SubscriptionForIssueModel(
          d,
          initialState.shareTypes.find((s) => s.sharetypeId === d.sharetypeId)!,
          { onTotalPriceChange: () => this.onTotalPriceForSubscriberChange() },
        ),
    );
    this.isSigningSelf = data.isSigningSelf;
    this.signers = data.signers.map((s) => new SubscriberSignerModel(s));
    this.signerChoiceModel = createSignerChoiceModel(this);
    initSigners(this);

    this.address = new StringFieldModel({
      value: data.address || this.addressFromExistingShareholder,
    });
    this.nationality = new StringFieldModel({
      value:
        (data.isExisting ? this.nationalityFromExistingShareholder : data.nationality) || norway,
    });
    const shownNationalId = data.isExisting ? this.ssnOrCompanyNumber : data.nationalId;
    this.nationalId = new StringFieldModel({
      value: shownNationalId,
      validator: (val) =>
        this.isCompany || withoutSpaces(val) === withoutSpaces(shownNationalId)
          ? undefined
          : nationalIdValidator(this.nationality.validatedValue, val),
    });

    this.kindContributionModel = createKindContributionModel(this, data?.kindContribution);
    this.debtContributionModel = createDebtContributionModel(
      this,
      defaultExcessAmountMethod,
      data?.debtContribution,
    );

    this.defaultExcessAmountMethod = defaultExcessAmountMethod;

    makeAutoObservable(this, {}, { autoBind: true });

    this.setupValidations();
  }

  onTotalPriceForSubscriberChange() {
    this.debtContributionModel.onTotalPriceForSubscriberChange();
  }

  setupValidations() {
    this.nameField.setRequired(true);
    this.nameField.setValidator(this.isCompany ? undefined : validateName);
    this.email.setRequired(!this.isExisting);
    this.organisationNumber.setRequired(this.isCompany && !this.isExisting);
    this.contactName.setRequired(this.isCompany && !this.isExisting);
  }

  setIsCompany(isCompany: boolean) {
    this.isCompany = isCompany;
    this.setupValidations();
  }

  get selectedSubscriptions(): SubscriptionForIssueModel[] {
    return this.subscriptions.filter((s) => this.selectedShareTypeIds().includes(s.sharetypeId));
  }

  get nationalitySelect() {
    return makeCountrySelect({
      value: this.nationality.validatedValue,
      onSelect: this.nationality.setAndCommitInputValue,
    });
  }

  get totalPriceForSubscriber(): number {
    return this.getTotalPriceForShareTypes(this.selectedShareTypeIds());
  }

  get totalPremiumForSubscriber() {
    const numberOfShares = this.getNumberOfSharesForShareTypes(this.selectedShareTypeIds());
    const totalShareCapital = Big(numberOfShares).times(this.shareNominalValue());
    return Big(this.totalPriceForSubscriber).minus(totalShareCapital);
  }

  getRemaining({
    isKindContributionActive,
    isDebtContributionActive,
  }: {
    isKindContributionActive: boolean;
    isDebtContributionActive: boolean;
  }): number {
    return Math.max(
      0,
      this.totalPriceForSubscriber -
        (isKindContributionActive ? this.kindContributionModel.value.toNumberOrZero() : 0) -
        (isDebtContributionActive
          ? this.debtContributionModel.maxAmountToBeConverted.toNumberOrZero()
          : 0),
    );
  }

  get hasContributionInKind() {
    return !this.kindContributionModel.isEmpty;
  }

  get hasCurrencyContributionInKind() {
    return this.kindContributionModel.isCurrencyContribution;
  }

  setContributionInKind(data?: KindContribution, address?: string) {
    this.kindContributionModel = createKindContributionModel(this, data);
    if (address !== undefined) {
      this.address.setAndCommitInputValue(address);
    }
  }

  get hasDebtContribution() {
    return !this.debtContributionModel.isEmpty;
  }

  setDebtContribution(data?: DebtContribution, address?: string) {
    this.debtContributionModel = createDebtContributionModel(
      this,
      this.defaultExcessAmountMethod,
      data,
    );
    if (address !== undefined) {
      this.address.setAndCommitInputValue(address);
    }
  }

  get existingShareholderData(): ShareholderDTO | undefined {
    return this.initialState.shareholders.find(
      (shareholder) => shareholder.id === this.shareholderId,
    );
  }

  get emailFromExistingShareholder(): string {
    return this.existingShareholderData?.email || "";
  }

  get addressFromExistingShareholder(): string {
    return getAddressAsString(this.existingShareholderData?.address);
  }

  get nationalityFromExistingShareholder(): string | undefined {
    return this.existingShareholderData?.nationality;
  }

  get ssnOrCompanyNumber(): string | undefined {
    const shareholder = this.existingShareholderData;
    const value = shareholder?.isCompany
      ? shareholder?.companyNumber
      : shareholder?.personInfo?.socialSecurityNumber;
    return value !== "" ? value : undefined;
  }

  get excludedActorIds(): string[] {
    return compact([
      this.shareholderId === undefined ? undefined : toShareholderReference(this.shareholderId),
      this.data.personReference,
      this.isCompany ? this.ssnOrCompanyNumber : undefined,
    ]);
  }

  get sharesForShareholder() {
    return (
      this.inputState?.sharesForShareholders.filter(
        (shareholder) => shareholder.shareholderId === this.shareholderId,
      ) ?? []
    );
  }

  get hasShareSubscriptions(): boolean {
    return this.subscriptions.some(
      (subscription) => subscription.numberOfShares.toNumberOrZero() > 0,
    );
  }

  initSubscription(
    sharetypeId: number,
    sharetypeCode: ShareTypeCode,
    subscriptionPrice: number,
  ): SubscriptionForIssueModel {
    const existing = this.subscriptions.find((x) => x.sharetypeId === sharetypeId);
    if (existing) {
      return existing;
    }
    this.subscriptions.push(
      new SubscriptionForIssueModel(
        {
          id: newUuid(),
          sharetypeId: sharetypeId,
          shareTypeCode: sharetypeCode,
          numberOfShares: 0,
          subscriptionPrice: subscriptionPrice,
        },
        this.initialState.shareTypes.find((s) => s.sharetypeId === sharetypeId)!,
        { onTotalPriceChange: () => this.onTotalPriceForSubscriberChange() },
      ),
    );
    return last(this.subscriptions)!;
  }

  getTotalPriceForShareTypes(selectedShareTypeIds: number[]) {
    return selectedShareTypeIds.reduce((acc, shareTypeId) => {
      return acc + this.getTotalPriceForShareTypeId(shareTypeId);
    }, 0);
  }

  getTotalPriceForShareTypeId(shareTypeId: number) {
    return this.subscriptions.find((s) => s.sharetypeId === shareTypeId)?.totalPrice ?? 0;
  }

  get currentTotalNumberOfShares(): number {
    return this.sharesForShareholder
      .reduce((sum, shareholder) => sum.plus(shareholder.numberOfShares), Big(0))
      .toNumber();
  }

  newTotalNumberOfShares(selectedShareTypeIds: number[]): number {
    return (
      this.currentTotalNumberOfShares + this.getNumberOfSharesForShareTypes(selectedShareTypeIds)
    );
  }

  getNumberOfSharesForShareTypes(selectedShareTypeIds: number[]): number {
    return selectedShareTypeIds
      .reduce(
        (sum, shareTypeId) => sum.plus(this.getNumberOfSharesForShareType(shareTypeId)),
        Big(0),
      )
      .toNumber();
  }

  getNumberOfSharesForShareType(shareTypeId: number) {
    return (
      this.subscriptions
        .find((s) => s.sharetypeId === shareTypeId)
        ?.numberOfShares.toNumberOrZero() ?? 0
    );
  }

  getTotalPriceForShareType(shareTypeCode: ShareTypeCode) {
    return this.subscriptions.find((s) => s.shareTypeCode === shareTypeCode)?.totalPrice ?? 0;
  }

  getSubscriptionForShareType(shareTypeId: number): SubscriptionForIssueModel | undefined {
    return this.subscriptions.find((s) => s.sharetypeId === shareTypeId);
  }

  getCurrentNumberOfSharesForShareType(shareTypeId: number): number {
    return (
      this.sharesForShareholder.find((d) => d.shareTypeId === shareTypeId)?.numberOfShares ?? 0
    );
  }

  getNewNumberOfSharesForShareType(shareTypeId: number): number {
    const calculatedShareForShareType = this.getCurrentNumberOfSharesForShareType(shareTypeId);
    const issuedShares = this.getNumberOfSharesForShareType(shareTypeId);
    return Big(calculatedShareForShareType).plus(issuedShares).toNumber();
  }

  get isSignerChoiceSelf() {
    return this.signerChoiceModel.value === SignerChoice.SELF;
  }

  addSigner(name: string = "", email: string = "") {
    this.signers.push(new SubscriberSignerModel({ id: newUuid(), name: name, email: email }));
  }

  addSubscriberAsSigner() {
    this.addSigner(
      this.isCompany && isNotBlank(this.contactName.validatedValue)
        ? this.contactName.validatedValue
        : this.nameField.validatedValue,
      this.email.validatedValue,
    );
  }

  removeSigner(signer: SubscriberSignerModel) {
    if (this.signers.includes(signer)) {
      this.signers.splice(this.signers.indexOf(signer), 1);
    }
  }

  refreshSignerChoice() {
    this.signerChoiceModel = createSignerChoiceModel(this);
  }

  get isEmpty(): boolean {
    return this.nameField.isEmpty;
  }

  get isValid(): boolean {
    return (
      this.nameField.isValid &&
      this.email.isValid &&
      this.organisationNumber.isValid &&
      this.nationalId.isValid
    );
  }

  validate(): boolean {
    this.nameField.validate();
    this.email.validate();
    this.organisationNumber.validate();
    this.nationalId.validate();
    this.subscriptions.forEach((d) => d.validate());
    return this.isValid;
  }

  toSigner(): Signer {
    return signer({
      id: this.subscriberid,
      reference: toSubscriberIdReference(this.subscriberid),
      name:
        this.isCompany && isNotBlank(this.contactName.validatedValue)
          ? this.contactName.validatedValue
          : this.nameField.validatedValue,
      email: this.email.validatedValue,
      signerType: SignerType.SUBSCRIPTION,
    });
  }

  get allSigners() {
    return compact([
      this.isSigningSelf &&
        new SubscriberSignerModel(
          {
            ...this.toSigner(),
            id: this.subscriberid,
          },
          true,
        ),
      ...this.signers,
    ]);
  }
}

export class SubscriptionForIssueModel {
  id: string;
  sharetypeId: number;
  shareTypeCode: ShareTypeCode;
  numberOfShares: NumberFieldModel;
  subscriptionPrice: NumberFieldModel;
  totalSubscriptionPrice: NumberFieldModel;

  _isUpdating = false;

  constructor(
    data: Subscription,
    readonly shareType: ShareType,
    readonly params: { onTotalPriceChange: () => void },
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.id = data.id;
    this.sharetypeId = data.sharetypeId;
    this.shareTypeCode = data.shareTypeCode;
    this.numberOfShares = new NumberFieldModel({
      value: data.numberOfShares,
      isPositiveOrZero: true,
      isInteger: true,
      onChange: () => {
        if (this._isUpdating) {
          return;
        }
        this._isUpdating = true;
        this.updateTotalPrice();
        this.params.onTotalPriceChange();
        this._isUpdating = false;
      },
    });
    this.subscriptionPrice = new NumberFieldModel({
      value: data.subscriptionPrice,
      isPositiveOrZero: true,
      onChange: () => {
        if (this._isUpdating) {
          return;
        }
        this._isUpdating = true;
        this.updateTotalPrice();
        this.params.onTotalPriceChange();
        this._isUpdating = false;
      },
    });
    this.totalSubscriptionPrice = new NumberFieldModel({
      value: this.totalPrice,
      isPositiveOrZero: true,
      onChange: (value) => {
        if (this._isUpdating) {
          return;
        }
        this._isUpdating = true;
        this.updateNumberOfShares(value ?? 0);
        this.params.onTotalPriceChange();
        this._isUpdating = false;
      },
    });
  }

  updateNumberOfShares(totalPrice: number) {
    this.numberOfShares.setAndCommitInputValue(
      Big(totalPrice).div(this.subscriptionPrice.toNumberOrZero()).toNumber(),
    );
  }

  updateTotalPrice() {
    this.totalSubscriptionPrice.setAndCommitInputValue(this.totalPrice);
  }

  get totalPrice(): number {
    const totalPrice = Big(this.numberOfShares.toNumberOrZero()).times(
      this.subscriptionPrice.toNumberOrZero(),
    );
    return roundWithPrecision(totalPrice).toNumber();
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid && this.subscriptionPrice.isValid;
  }

  validate(): boolean {
    this.numberOfShares.validate();
    this.subscriptionPrice.validate();
    return this.isValid;
  }
}

export class SubscriberSignerModel {
  id: string;
  name: StringFieldModel;
  email: StringFieldModel;

  constructor(
    data: SubscriberSigner,
    readonly isReadonly = false,
  ) {
    this.id = data.id;
    this.name = new StringFieldModel({ value: data.name, required: true, validator: validateName });
    this.email = createEmailField({ value: data.email, required: true });
  }

  get isValid(): boolean {
    return this.isReadonly ? true : this.name.isValid && this.email.isValid;
  }

  validate(): boolean {
    if (!this.isReadonly) {
      this.name.validate();
      this.email.validate();
    }
    return this.isValid;
  }

  toSubscriberSigner(): SubscriberSigner {
    return {
      id: this.id,
      name: this.name.validatedValue,
      email: this.email.validatedValue ?? "",
    };
  }

  toSigner(subscriberId: SubscriberId): Signer {
    return signer({
      id: this.id,
      reference: toSubscriberIdReference(subscriberId),
      name: this.name.validatedValue,
      email: this.email.validatedValue ?? "",
      signerType: SignerType.SUBSCRIPTION,
    });
  }
}

const preferentialRightWaivedReasonCaptions: { [key in PreferentialRightWaivedType]: TMessage } = {
  [PreferentialRightWaivedType.SHAREHOLDERS_IN_UNDERSTANDING]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.existingShareholdersAgrees",
  },
  [PreferentialRightWaivedType.DEEMED_NECESSARY]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.necessaryToIncreaseCapital",
  },
  [PreferentialRightWaivedType.ADDED_EXPLANATION]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.other",
  },
};

export function calculateNumberOfSharesFromSubscriptions(
  subscribers: SubscriberForIssueModel[],
  selectedShareTypes: ShareTypeOption[],
): [FixedModel, number][] {
  return selectedShareTypes
    .filter(
      (m) =>
        m.model.issueMode.type === "fixed" && m.model.issueMode.calculateFromSubscriptions.value,
    )
    .map((m) => [
      m.model.fixed,
      sumBy(subscribers, (s) => s.getNumberOfSharesForShareType(m.sharetypeId)),
    ]);
}

export function calculateSubscriptionsFromNumberOfShares(
  subscribers: SubscriberForIssueModel[],
  selectedShareTypes: ShareTypeOption[],
): [SubscriptionForIssueModel, number][] {
  if (subscribers.length !== 1) return [];
  const [subscriber] = subscribers;
  return selectedShareTypes
    .filter(
      (m) =>
        m.model.issueMode.type === "fixed" && !m.model.issueMode.calculateFromSubscriptions.value,
    )
    .flatMap((m) => {
      const subscription = subscriber.getSubscriptionForShareType(m.sharetypeId);
      return subscription
        ? [[subscription, m.model.fixed.numberOfShares.toNumber() ?? 0] as const]
        : [];
    });
}

export class ShareIssueModel {
  type: IncreaseOfShareCapitalType.shareIssue;
  nominalValue: NumberFieldModel;
  preferentialRight: BooleanFieldModel;
  preferentialRightTransferable: BooleanFieldModel;
  preferentialRightOversubscribe: BooleanFieldModel;
  preferentialBoardRightsToSellSubscription: BooleanFieldModel;
  externalSubscribers: BooleanFieldModel;
  preferentialRightWaivedReasonText: StringFieldModel;
  preferentialRightWaivedReasonTexts: LocalizedStringFieldModel[];
  shareholderRights: ShareholderRightsModel;
  isGuaranteeConsortiumEstablished: BooleanFieldModel;
  guaranteeConsortiumCommission: GuaranteeConsortiumCommissionModel;

  inputState?: InputState;

  selectedShareTypes: OptionFieldModel<ShareTypeOption>;

  subscribers: SubscriberForIssueModel[] = []; // rename this to subscriptions?
  preferentialIssueSubscribers: SubscriberForIssueModel[] = [];
  preferentialRightWaivedReasonModel: RadioFieldModel<PreferentialRightWaivedType | undefined>;

  addShareholdersFromActorsModel?: ActorsSelectorModel;
  newShareClassController?: NewShareClassController;

  defaultExcessAmountMethod: ExcessAmountMethod;

  selectSubscriberFn?: (s: SubscriberForIssueModel) => void;

  pendingShareholderEmailUpdates: { shareholderId: number; email: string }[] = [];

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<IncreaseOfShareCapital>,
    defaultExcessAmountMethod: ExcessAmountMethod,
    updateRequiredDependencies: () => void,
    public data?: ShareIssue,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.inputState = context.inputState;

    this.type = IncreaseOfShareCapitalType.shareIssue;
    this.nominalValue = new NumberFieldModel({
      value: data?.nominalValue ?? this.inputState?.sharenominalValue,
      isPositive: true,
      onChange: updateRequiredDependencies,
    });
    this.preferentialRight = new BooleanFieldModel({
      value: data?.preferentialRight,
      onChange: updateRequiredDependencies,
    });
    this.preferentialRightTransferable = new BooleanFieldModel({
      value: data?.preferentialRightTransferable,
    });
    this.preferentialRightOversubscribe = new BooleanFieldModel({
      value: data?.preferentialRightOversubscribe,
    });
    this.preferentialBoardRightsToSellSubscription = new BooleanFieldModel({
      value: data?.preferentialBoardRightsToSellSubscription,
    });
    this.externalSubscribers = new BooleanFieldModel({ value: data?.externalSubscribers });
    this.preferentialRightWaivedReasonText = new StringFieldModel({
      value: data?.preferentialRightWaivedReasonText,
    });
    this.preferentialRightWaivedReasonTexts = createTextModels(
      data?.preferentialRightWaivedReasonTexts,
    );
    this.shareholderRights = new ShareholderRightsModel({
      value: data?.shareholderRights ?? ShareholderRights.FROM_REGISTRATION_DATE,
      required: true,
    });
    this.isGuaranteeConsortiumEstablished = new BooleanFieldModel({
      value: data?.isGuaranteeConsortiumEstablished ?? false,
      required: true,
      onChange: updateRequiredDependencies,
    });
    this.guaranteeConsortiumCommission = new GuaranteeConsortiumCommissionModel(
      data?.guaranteeConsortiumCommission,
    );

    this.selectedShareTypes = this.createSelectedShareTypeModel(data?.shareIssuePerShareTypes);

    this.preferentialRightWaivedReasonModel = new RadioFieldModel<
      PreferentialRightWaivedType | undefined
    >({
      value: data?.preferentialRightWaivedReason,
      options: Object.values(PreferentialRightWaivedType),
      idFn: identity,
      labelFn: (value) =>
        value ? preferentialRightWaivedReasonCaptions[value] : { translated: "" },
    });

    this.subscribers = this.toSubscriberForIssueModels(data?.subscribers ?? []);
    this.preferentialIssueSubscribers = this.toPreferentialIssueModel(
      data?.preferentialIssueSubscribers ?? [],
    );
    this.defaultExcessAmountMethod = defaultExcessAmountMethod;
  }

  init(): CleanupFunction {
    return autorun(() => {
      const toUpdate = calculateNumberOfSharesFromSubscriptions(
        this.preferentialRight.value ? this.preferentialIssueSubscribers : this.subscribers,
        this.selectedShareTypes.selected,
      );
      const toUpdateSubscriptions = calculateSubscriptionsFromNumberOfShares(
        this.subscribers,
        this.selectedShareTypes.selected,
      );
      runInAction(() => {
        toUpdate.forEach(([model, numberOfShares]) => {
          model.numberOfShares.setAndCommitInputValue(numberOfShares);
        });
        toUpdateSubscriptions.forEach(([subscription, numberOfShares]) => {
          subscription.numberOfShares.setAndCommitInputValue(numberOfShares);
        });
      });
    });
  }

  setSelectSubscriberFn(fn: ShareIssueModel["selectSubscriberFn"]) {
    this.selectSubscriberFn = fn;
  }

  get existingShareholders() {
    return toExistingShareholders(this.context.initialState.shareholders);
  }

  get soleShareholder(): ExistingShareholder | undefined {
    const shareholders = shareholdersWithShares(
      this.context.initialState.shareholders,
      this.inputState?.sharesForShareholders,
    );
    if (shareholders.length !== 1) return undefined;
    return this.existingShareholders.find(
      (s) =>
        s.id === shareholders[0].id && !this.subscribers.some((sub) => sub.shareholderId === s.id),
    );
  }

  addSoleShareholder() {
    const soleShareholder = this.soleShareholder;
    if (soleShareholder) {
      this.addSubscribers(mapExistingShareholdersToSubscriberForIssue([soleShareholder]));
      this.dispatch({
        type: "show-alert",
        message: {
          key: "decisions.increaseShareCapital.manageSubscribers.soleShareholderAdded",
          params: { name: soleShareholder.name },
        },
        messageType: "success",
      });
    }
  }

  get selectedShareTypeIds() {
    return this.selectedShareTypes.selected.map((d) => d.sharetypeId);
  }

  get totalShareTypePrice() {
    return this.selectedShareTypes.selected
      .reduce((sum, shareTypeOption) => sum.plus(shareTypeOption.model.maxTotalPrice), Big(0))
      .toNumber();
  }

  get minNumberOfShares() {
    return this.selectedShareTypes.selected
      .reduce((sum, shareTypeOption) => sum.plus(shareTypeOption.model.minNumberOfShares), Big(0))
      .toNumber();
  }

  get maxNumberOfShares() {
    return this.selectedShareTypes.selected
      .reduce((sum, shareTypeOption) => sum.plus(shareTypeOption.model.maxNumberOfShares), Big(0))
      .toNumber();
  }

  get shareTypeCodes() {
    return Object.values(ShareTypeCode).filter(
      (s) =>
        !this.selectedShareTypes.options.some((st) => st.sharetypeCode === s) &&
        s !== ShareTypeCode.UNKNOWN,
    );
  }

  get numberOfSharesForShareType() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.context.initialState.shareTypes,
      this.inputState?.numberOfShares,
    );
  }

  get newNumberOfShares() {
    return this.maxNumberOfShares + this.currentNumberOfShares;
  }

  get currentNumberOfShares() {
    return total(this.inputState?.numberOfShares ?? []);
  }

  get currentShareNominalValue() {
    return this.currentNumberOfShares === 0
      ? this.nominalValue.toNumberOrZero()
      : (this.inputState?.sharenominalValue ?? 0);
  }

  createSelectedShareTypeModel(data?: ShareIssuePerShareType[]) {
    const shareIssueData = (id: number) => data?.find((d) => d.sharetypeId === id);

    return ShareIssueModel.createSelectedShareTypes(
      data?.map((s) => ({
        sharetypeId: s.sharetypeId,
        sharetypeCode: s.sharetypeCode,
        shareType: this.getShareType(s.sharetypeId),
        isNewShareType: false,
        model: new ShareIssuePerShareTypeModel(
          this.dispatch,
          s.sharetypeId,
          s.sharetypeCode,
          this.getShareType(s.sharetypeId),
          () => this.currentShareNominalValue,
          this.onShareTypeChange,
          s,
        ),
      })),
      this.getSharetypeOptions(shareIssueData, this.dispatch),
    );
  }

  onShareTypeChange() {
    this.setSubscribersForIssue();
    this.selectedShareTypes.selected.forEach(() => {
      this.preferentialIssueSubscribers.forEach((subscriber) => {
        subscriber.onTotalPriceForSubscriberChange();
      });
      this.subscribers.forEach((subscriber) => {
        subscriber.onTotalPriceForSubscriberChange();
      });
    });
  }

  private getShareType(shareTypeId: number) {
    return this.context.initialState.shareTypes.find((d) => d.sharetypeId === shareTypeId)!;
  }

  onInputStateChange(inputState?: InputState) {
    const isNumberOfSharesChanged = !isEqual(
      this.inputState?.numberOfShares,
      inputState?.numberOfShares,
    );
    this.inputState = inputState;
    if (isNumberOfSharesChanged) {
      this.selectedShareTypes = this.createSelectedShareTypeModel(toShareIssueForShareTypes(this));
    }
    if (this.currentNumberOfShares === 0) {
      this.preferentialRight.setValue(false);
    }
  }

  get shareSumByType(): Record<string, number> {
    return this.numberOfSharesForShareType
      .filter((s) => s.shareTypeCode !== undefined)
      .reduce(
        (pre, type) => ({
          ...pre,
          [type.shareTypeCode!]: type.numberOfShares,
        }),
        {},
      );
  }

  get shareholders(): ShareholderDTO[] {
    return this.context.initialState.shareholders;
  }

  private getSharetypeOptions(
    shareIssueData: (id: number) => ShareIssuePerShareType | undefined,
    dispatch: DbotEventDispatcher,
  ): ShareTypeOption[] {
    return compact(
      Object.values(ShareTypeCode).map((shareTypeCode) => {
        const s = this.numberOfSharesForShareType.find((x) => x.shareTypeCode === shareTypeCode);
        if (s) {
          return {
            sharetypeId: s.shareTypeId,
            sharetypeCode: s.shareTypeCode!,
            shareType: this.getShareType(s.shareTypeId),
            isNewShareType: shareIssueData(s.shareTypeId)?.isNewShareType || false,
            model: new ShareIssuePerShareTypeModel(
              dispatch,
              s.shareTypeId,
              s.shareTypeCode!,
              this.getShareType(s.shareTypeId),
              () => this.currentShareNominalValue,
              this.onShareTypeChange,
              shareIssueData(s.shareTypeId),
            ),
          };
        }
      }),
    );
  }

  setSubscribersModel(subscribers: SubscriberForIssue[], isPreferentialRight: boolean) {
    if (isPreferentialRight) {
      this.preferentialIssueSubscribers = this.toSubscriberForIssueModels(subscribers);
    } else {
      this.subscribers = this.toSubscriberForIssueModels(subscribers);
    }
  }

  deleteSubscriber(subscriber: SubscriberForIssueModel) {
    this.subscribers = this.subscribers.filter((value) => value !== subscriber);
  }

  openAddNewShareholderFromPortfolioDialog() {
    this.addShareholdersFromActorsModel = createAddShareholdersFromActorsModel(
      this.context.orgNo,
      this.dispatch,
      this.subscribers,
      this.existingShareholders,
      this.addSubscribers,
      this.closeAddShareholderFromActorsDialog,
    );
  }

  closeAddShareholderFromActorsDialog() {
    this.addShareholdersFromActorsModel = undefined;
  }

  searchCompanies(input: string) {
    this.dispatch({ type: "dbot/search-companies", input, source: this });
  }

  addNewSubscriber(isCompany: boolean) {
    const models = this.addSubscribers([this.createSubscriber({ isCompany })]);
    defer(() => {
      this.selectSubscriberFn?.(models[0]);
    });
  }

  addSubscribers(newSubscribers: SubscriberForIssue[]): SubscriberForIssueModel[] {
    const selectedSubscribers = newSubscribers.map((s) => this.createSubscriber(s));
    const currentShareholderIds = new Set(
      this.subscribers.filter((s) => s.shareholderId).map((s) => s.shareholderId),
    );
    const models = this.toSubscriberForIssueModels(selectedSubscribers).filter(
      (m) => !currentShareholderIds.has(m.shareholderId),
    );
    this.subscribers.push(...models);
    return models;
  }

  addSubscriberModels(models: SubscriberForIssueModel[]) {
    this.subscribers.push(...models);
  }

  createSubscriber(data?: Partial<SubscriberForIssue>) {
    return createSubscriberForIssue({
      ...data,
      subscriptions: this.selectedShareTypes.selected.map<Subscription>((s) => {
        const subscription = data?.subscriptions?.find((x) => x.sharetypeId === s.sharetypeId);
        return {
          id: subscription?.id ?? newUuid(),
          sharetypeId: subscription?.sharetypeId ?? s.sharetypeId,
          shareTypeCode: subscription?.shareTypeCode ?? s.sharetypeCode,
          numberOfShares: subscription?.numberOfShares ?? 0,
          subscriptionPrice: subscription?.subscriptionPrice ?? s.model.sharePrice.toNumberOrZero(),
        };
      }),
    });
  }

  toSubscriberForIssueModelWithSelectedShareTypes(
    data: SubscriberForIssue[],
  ): SubscriberForIssueModel[] {
    const subscribers = data.map((subscriber) => {
      return {
        ...subscriber,
        subscriptions: subscriber.subscriptions.map((subscription) => {
          const selectedShareTypeModel = this.selectedShareTypes.selected.find(
            (d) => d.sharetypeId === subscription.sharetypeId,
          )?.model;
          return {
            ...subscription,
            subscriptionPrice: selectedShareTypeModel?.isIndividualSharePrice.value
              ? subscription.subscriptionPrice
              : (selectedShareTypeModel?.sharePrice.toNumberOrZero() ??
                subscription.subscriptionPrice),
          };
        }),
      };
    });
    return this.toSubscriberForIssueModels(subscribers);
  }

  toSubscriberForIssueModels(data: SubscriberForIssue[]): SubscriberForIssueModel[] {
    return data.map((d) => this.toSubscriberForIssueModel(d));
  }

  toSubscriberForIssueModel(data: SubscriberForIssue): SubscriberForIssueModel {
    return new SubscriberForIssueModel(
      data,
      this.context.initialState,
      this.inputState,
      () => this.selectedShareTypeIds,
      this.context.aiClient,
      this.context.documentLanguages,
      this.defaultExcessAmountMethod,
      () => this.currentShareNominalValue,
    );
  }

  updateShareholderEmail(shareholderId: number, email: string) {
    this.dispatch({
      type: "dbot/update-shareholder-email",
      shareholderId,
      email,
      onFulfilled: (result) => {
        if (isFailure(result)) {
          this.dispatch(showError(result.error));
        }
      },
    });
  }

  initShareholderEmailBatchUpdates() {
    this.pendingShareholderEmailUpdates = [];
  }

  queueShareholderEmailUpdate(shareholderId: number, email: string) {
    this.pendingShareholderEmailUpdates.push({ shareholderId, email });
  }

  updateShareholderEmailBatch() {
    if (this.pendingShareholderEmailUpdates.length) {
      this.dispatch({
        type: "dbot/update-shareholder-email-batch",
        updates: this.pendingShareholderEmailUpdates.slice(),
        onFailure: () => [showError({ key: "error.update-email" })],
      });
      this.pendingShareholderEmailUpdates = [];
    }
  }

  setSubscribersForIssue() {
    this.preferentialIssueSubscribers = this.toPreferentialIssueModel(
      this.preferentialIssueSubscribers.map((d) => toSubscriberForIssue(d)),
    );
    this.subscribers = this.toSubscriberForIssueModelWithSelectedShareTypes(
      this.subscribers.map((d) => toSubscriberForIssue(d)),
    );
    if (this.hasIndividualSharePrice) {
      this.preferentialRight.setValue(false);
    }
    this.selectedShareTypes.selected.forEach((shareTypeOption) => {
      this.preferentialIssueSubscribers.forEach((subscriber) => {
        subscriber.initSubscription(
          shareTypeOption.sharetypeId,
          shareTypeOption.sharetypeCode,
          shareTypeOption.model.sharePrice.toNumberOrZero(),
        );
      });
      this.subscribers.forEach((subscriber) => {
        subscriber.initSubscription(
          shareTypeOption.sharetypeId,
          shareTypeOption.sharetypeCode,
          shareTypeOption.model.sharePrice.toNumberOrZero(),
        );
      });
    });
  }

  get hasIndividualSharePrice() {
    return this.selectedShareTypes.selected.some((s) => s.model.isIndividualSharePrice.value);
  }

  toPreferentialIssueModel(data: SubscriberForIssue[]): SubscriberForIssueModel[] {
    const subscribers = subscribersForIssueFromProRataCalculation(
      data,
      this.selectedShareTypes.selected,
      this.currentNumberOfShares,
      this.shareholders,
      this.inputState?.sharesForShareholders ?? [],
    );
    return this.toSubscriberForIssueModels(subscribers);
  }

  hasContributionInKind(s: SubscriberForIssue) {
    return s && s.kindContribution !== undefined;
  }

  private static createSelectedShareTypes(
    selected: ShareTypeOption[] | undefined,
    options: ShareTypeOption[],
  ) {
    return new OptionFieldModel({
      value: selected,
      required: true,
      options: options,
      idFn: (v) => v.sharetypeId.toString(),
      labelFn: () => ({ translated: "" }),
    });
  }

  toggleSelectedShareType(option: ShareTypeOption) {
    this.selectedShareTypes.toggle(option);
    this.onShareTypeChange();
  }

  openNewSharetypeDialog(http: AxiosInstance, language: Language) {
    this.newShareClassController = new NewShareClassController(
      this.context.orgNo,
      new ShareClassApi(http),
      this.closeNewSharetypeDialog,
      this.shareTypeCodes,
      language,
      this.addNewSharetype,
    );
  }

  closeNewSharetypeDialog() {
    this.newShareClassController = undefined;
  }

  addNewSharetype(shareType: ShareType) {
    const newOption: ShareTypeOption = {
      sharetypeId: shareType.sharetypeId,
      sharetypeCode: shareType.shareTypeCode,
      shareType,
      isNewShareType: true,
      model: new ShareIssuePerShareTypeModel(
        this.dispatch,
        shareType.sharetypeId,
        shareType.shareTypeCode,
        shareType,
        () => this.currentShareNominalValue,
        this.onShareTypeChange,
      ),
    };
    const selected = [...this.selectedShareTypes.selected, newOption];
    const options = [...this.selectedShareTypes.options, newOption];
    this.selectedShareTypes = ShareIssueModel.createSelectedShareTypes(selected, options);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isSelectedShareTypesMissingValues() {
    return (
      !this.selectedShareTypes.isValid ||
      this.selectedShareTypes.selected.some((d) => d.model.isMissingValues)
    );
  }

  get isSelectedShareTypesValid() {
    return (
      this.selectedShareTypes.isValid &&
      this.selectedShareTypes.selected.every((d) => d.model.isValid)
    );
  }

  get isValid(): boolean {
    return (
      this.nominalValue.isValid &&
      this.isSelectedShareTypesValid &&
      this.preferentialRight.isValid &&
      this.preferentialRightTransferable.isValid &&
      this.preferentialRightOversubscribe.isValid &&
      this.preferentialBoardRightsToSellSubscription.isValid &&
      this.externalSubscribers.isValid &&
      this.preferentialRightWaivedReasonModel.isValid &&
      this.shareholderRights.isValid &&
      this.isGuaranteeConsortiumEstablished.isValid &&
      this.guaranteeConsortiumCommission.isValid
    );
  }

  validateSelectedShareTypes(): boolean {
    this.selectedShareTypes.validate();
    this.selectedShareTypes.selected.every((d) => d.model.validate());
    return this.isSelectedShareTypesValid;
  }

  validate(): boolean {
    this.nominalValue.validate();
    this.validateSelectedShareTypes();
    this.preferentialRight.validate();
    this.preferentialRightTransferable.validate();
    this.preferentialRightOversubscribe.validate();
    this.preferentialBoardRightsToSellSubscription.validate();
    this.externalSubscribers.validate();
    this.preferentialRightWaivedReasonModel.validate();
    this.shareholderRights.validate();
    this.isGuaranteeConsortiumEstablished.validate();
    this.guaranteeConsortiumCommission.validate();
    return this.isValid;
  }
}

export class ContributionPerShareTypeModel {
  contributionPerShare: NumberFieldModel;
  readonly data: ContributionPerShareType;

  constructor(
    data: ContributionPerShareType,
    private context: DecisionContext<IncreaseOfShareCapital>,
    readonly increaseInNominalValue: NumberFieldModel,
    readonly shareType: ShareType,
    readonly numberOfShares: number,
    params: { onChange: () => void },
  ) {
    this.data = data;
    this.contributionPerShare = new NumberFieldModel({
      value: data?.contributionPerShare,
      isPositiveOrZero: true,
      required: true,
      validator: (value) => {
        return value !== undefined && this.increaseInNominalValue.toNumberOrZero() > value
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.shareContribution-helperText",
              params: {
                currency: CURRENCY_NOK,
                newShareNominalValue: this.increaseInNominalValue.toNumberOrZero(),
              },
            }
          : undefined;
      },
      onChange: params.onChange,
    });
    makeAutoObservable(this);
  }

  get isValid(): boolean {
    return this.contributionPerShare.isValid;
  }

  validate(): boolean {
    return this.contributionPerShare.validate();
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get premium(): TMessageParam {
    const premium = Big(this.contributionPerShare.toNumberOrZero())
      .minus(this.increaseInNominalValue.toNumberOrZero())
      .toNumber();
    switch (true) {
      case premium === 0:
        return { type: "text", value: "-" };
      default:
        return { type: "number", value: premium };
    }
  }

  get total(): TMessageParam {
    return {
      type: "number",
      value: Big(this.contributionPerShare.toNumberOrZero()).times(this.numberOfShares).toNumber(),
    };
  }

  toModel(): ContributionPerShareType {
    return {
      contributionPerShare: this.contributionPerShare.toNumber(),
      sharetypeId: this.data.sharetypeId,
      name: this.data.name,
    };
  }
}

export class ShareIssuePerShareTypeModel {
  fixed: FixedModel;
  interval: IntervalModel;
  issueMode: ShareIssueModeModel;
  sharePrice: NumberFieldModel;
  isIndividualSharePrice: BooleanFieldModel;
  isSelected: boolean;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    public readonly sharetypeId: number,
    public readonly sharetypeCode: string,
    public readonly shareType: ShareType,
    public readonly shareNominalValue: () => number,
    public onChange: () => void,
    public data?: ShareIssuePerShareType,
  ) {
    this.fixed = new FixedModel(asFixed(data?.issueMode));
    this.interval = new IntervalModel(asInterval(data?.issueMode));
    this.issueMode = data?.issueMode.type === "interval" ? this.interval : this.fixed;
    this.sharePrice = new NumberFieldModel({
      value: data?.sharePrice ?? shareNominalValue(),
      isPositiveOrZero: true,
      required: true,
      validator: (value) => {
        return value !== undefined && this.shareNominalValue() > value
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.newShares.subscriptionValue.helperText",
              params: {
                currency: CURRENCY_NOK,
                shareNominalValue: this.shareNominalValue(),
              },
            }
          : undefined;
      },
      onChange: onChange,
    });
    this.isIndividualSharePrice = new BooleanFieldModel({
      value: data?.isIndividualSharePrice,
    });
    this.isSelected = data !== undefined;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  changeIssueMode(type: string) {
    if (type === "interval") {
      this.issueMode = this.interval;
    } else {
      this.issueMode = this.fixed;
    }
    this.onChange();
  }

  get maxTotalPrice(): number {
    return Big(this.maxNumberOfShares).mul(this.sharePrice.toNumberOrZero()).toNumber();
  }

  get minNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares.toNumberOrZero();
      case "interval":
        return this.issueMode.from.toNumberOrZero();
    }
  }

  get maxNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares.toNumberOrZero();
      case "interval":
        return this.issueMode.to.toNumberOrZero();
    }
  }

  get showShareIssueFixed() {
    return this.issueMode.type === "fixed";
  }

  get showShareIssueInterval() {
    return this.issueMode.type === "interval";
  }

  get isMissingValues(): boolean {
    return this.issueMode.isMissingValues || this.sharePrice.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.issueMode.isValid && this.sharePrice.isValid;
  }

  validate(): boolean {
    this.issueMode.validate();
    this.sharePrice.validate();
    return this.isValid;
  }
}

function asFixed(data: ShareIssueMode | undefined): Fixed | undefined {
  if (data?.type === "fixed") {
    return data;
  }
}

function asInterval(data: ShareIssueMode | undefined): Interval | undefined {
  if (data?.type === "interval") {
    return data;
  }
}

export type ShareIssueModeModel = FixedModel | IntervalModel;

export class FixedModel {
  type: "fixed";
  numberOfShares: NumberFieldModel;
  calculateFromSubscriptions: BooleanFieldModel;

  constructor(data?: Fixed) {
    this.type = "fixed";
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
      required: true,
      isInteger: true,
    });
    this.calculateFromSubscriptions = new BooleanFieldModel({
      value: data?.calculateFromSubscriptions,
    });
    makeAutoObservable(this);
  }

  get isMissingValues(): boolean {
    return this.numberOfShares.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate();
  }
}

export class IntervalModel {
  type: "interval";
  from: NumberFieldModel;
  to: NumberFieldModel;

  constructor(data?: Interval) {
    this.type = "interval";
    this.from = new NumberFieldModel({
      value: data?.from,
      required: true,
      isInteger: true,
      isPositiveOrZero: true,
    });
    this.to = new NumberFieldModel({
      value: data?.to,
      isPositiveOrZero: true,
      required: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }

  get isMissingValues(): boolean {
    return this.from.toNumber() === undefined || this.to.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.from.isValid && this.to.isValid;
  }

  validate(): boolean {
    this.from.validate();
    this.to.validate();
    return this.isValid;
  }
}

class GuaranteeConsortiumCommissionModel {
  numberOfShares: NumberFieldModel;
  percentOfGuaranteedAmount: NumberFieldModel;

  constructor(data?: GuaranteeConsortiumCommission) {
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
      isInteger: true,
    });
    this.percentOfGuaranteedAmount = new NumberFieldModel({
      value: data?.percentOfGuaranteedAmount,
      isPositiveOrZero: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }

  setRequired(isRequired: boolean | undefined) {
    this.numberOfShares.setRequired(isRequired);
    this.percentOfGuaranteedAmount.setRequired(isRequired);
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid && this.percentOfGuaranteedAmount.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate() && this.percentOfGuaranteedAmount.validate();
  }
}

export class PeriodForSubscriptionModel {
  from: LocalDateFieldModel;
  to: LocalDateFieldModel;

  constructor(data?: PeriodForSubscription) {
    this.from = new LocalDateFieldModel({ value: data?.from });
    this.to = new LocalDateFieldModel({ value: data?.to });
    makeAutoObservable(this);
  }

  setRequired(isSubscriptionPeriod: boolean | undefined) {
    this.from.setRequired(isSubscriptionPeriod);
    this.to.setRequired(isSubscriptionPeriod);
  }

  get isValid(): boolean {
    return this.from.isValid && this.to.isValid;
  }

  validate(): boolean {
    return this.from.validate() && this.to.validate();
  }
}

export class ShareholderRightsModel {
  readonly params: { value?: ShareholderRights; required?: boolean };

  value?: ShareholderRights;
  error?: TMessage = undefined;

  constructor(params: { value?: ShareholderRights; required?: boolean } = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value = params.value;
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate() {
    if (this.params.required && this.value === undefined) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
  }

  setValue(value: string) {
    if (value === "FROM_REGISTRATION_DATE") this.value = ShareholderRights.FROM_REGISTRATION_DATE;
    else if (value === "FROM_SUBSCRIPTION_DATE")
      this.value = ShareholderRights.FROM_SUBSCRIPTION_DATE;
  }
}

export type ViewSubscribersDialogModel =
  | { type: "status"; subscriberModel: SubscribersStatusModel }
  | { type: "allocation"; subscriberModel: ShareAllocationRoundsModel };

export class ShareAllocationRoundsModel {
  allocationResponse: Result<ShareAllocationRoundsResponse, TMessage> = pending();
  saveAllocationResult: Result = ok();
  allocationsRounds: ShareAllocationRoundModel[];
  selectedAllocationRound: ShareAllocationRoundModel;
  isAllocationCompleted: boolean;

  constructor(
    readonly orgNo: string,
    readonly decisionId: string,
    readonly dispatch: DbotEventDispatcher,
    readonly closeDialog: () => void,
    readonly shareTypes: ShareType[],
    readonly inputState?: InputState,
    data?: ShareAllocationRoundsResponse,
    readonly isSentToBR: boolean = false,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.decisionId = decisionId;
    this.allocationsRounds =
      data?.allocationRounds.map(
        (d) => new ShareAllocationRoundModel(d, this.getShareType(d), this.inputState),
      ) ?? [];
    this.selectedAllocationRound = this.allocationsRounds[0];
    this.isAllocationCompleted = data?.isAllocationCompleted ?? false;
  }

  init(): void {
    this.dispatch({ type: "dbot/fetch-increase-of-share-capital-subscribers-allocation" });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/fetch-increase-of-share-capital-subscribers-allocation-result":
        this.toModel(event.result);
        break;
      case "dbot/save-share-allocation-result":
        this.dispatch(
          updateFollowUpStatusEvent(this.orgNo, this.decisionId, {
            type: FollowUpType.allocatedShares,
          }),
        );
        this.saveAllocationResult = ok();
        if (isOk(event.result)) {
          this.isAllocationCompleted = event.result.data;
        }
        break;
    }
  }

  toModel(result: Ok<ShareAllocationRoundsResponse> | Failure<TMessage>) {
    this.allocationResponse = result;
    if (isOk(this.allocationResponse)) {
      this.allocationsRounds = this.allocationResponse.data.allocationRounds.map(
        (d) => new ShareAllocationRoundModel(d, this.getShareType(d), this.inputState),
      );
      this.isAllocationCompleted = this.allocationResponse.data.isAllocationCompleted;
      this.selectedAllocationRound = this.allocationsRounds[0];
    }
  }

  private getShareType(d: { shareTypeId?: number; shareTypeCode: ShareTypeCode }) {
    if (d.shareTypeId) {
      return this.shareTypes.find((s) => s.sharetypeId === d.shareTypeId)!;
    } else {
      return this.shareTypes.find((s) => s.shareTypeCode === d.shareTypeCode && !s.historical)!;
    }
  }

  allocateAllOfferedShares() {
    this.allocationsRounds.map((round) => round.allocateAllOfferedSharesInRound());
  }

  get isAllSharesAllocatedInRounds() {
    return this.allocationsRounds.every((d) => d.isAllSharesAllocated);
  }

  get shareTypeCodes() {
    return Array.from(new Set(this.allocationsRounds.map((d) => d?.shareTypeCode)));
  }

  setSelectedAllocation(value: ShareAllocationRoundModel) {
    this.selectedAllocationRound = value;
  }

  get isEmpty() {
    return this.shareTypeCodes.length === 0 && this.allocationsRounds.length === 0;
  }

  prepareData(): ShareAllocationRound[] {
    return this.allocationsRounds.map((data) => data.prepareData());
  }

  saveAllocation(isCompleted: boolean) {
    this.saveAllocationResult = pending();
    this.dispatch({
      type: "dbot/save-share-allocation",
      allocationRounds: this.prepareData(),
      isAllocationCompleted: isCompleted,
    });
  }

  saveAndClose() {
    if (!this.isAllocationCompleted) this.saveAllocation(this.isAllocationCompleted);
    this.closeDialog();
  }

  close() {
    this.closeDialog();
  }

  get canConfirmAllocation() {
    return this.hasAllocatedValidNumberOfShares && this.isAllSharesAllocatedInRounds;
  }

  get hasAllocatedValidNumberOfShares() {
    return this.allocationsRounds.every((d) => d.remainingSharesToAllocate >= 0);
  }

  toggleAllocationStatusAndSave() {
    this.saveAllocation(!this.isAllocationCompleted);
  }
}

export class ShareAllocationRoundModel {
  id: string;
  projectId: string;
  documentId: string;
  roundNumber: number;
  sharetypeId?: number;
  shareTypeCode: ShareTypeCode;
  minShares: number;
  maxShares: number;
  allocations: ShareAllocationModel[];
  isCompleted: boolean;

  constructor(
    data: ShareAllocationRound,
    readonly shareType: ShareType,
    readonly inputState?: InputState,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.id = data.id;
    this.projectId = data.projectId;
    this.documentId = data.documentId;
    this.roundNumber = data.roundNumber;
    this.sharetypeId = data.sharetypeId;
    this.shareTypeCode = data.shareTypeCode;
    this.minShares = data.minShares;
    this.maxShares = data.maxShares;
    this.allocations = data.allocations.map((d) => new ShareAllocationModel(d));
    this.isCompleted = data.isCompleted;
  }

  allocateAllOfferedSharesInRound() {
    this.allocations.map((allocation) => {
      allocation.allocatedShares.setAndCommitInputValue(allocation.offeredShares);
    });
  }

  get isAllSharesAllocated() {
    return this.allocations.every((d) => d.isAllocatedShares);
  }

  get isSharesFixed() {
    return this.minShares === this.maxShares;
  }

  currentNumberOfSharesForSubscriber(id?: number) {
    return (
      this.inputState?.sharesForShareholders.find(
        (d) => d.shareholderId === id && d.shareTypeId === this.sharetypeId,
      )?.numberOfShares ?? 0
    );
  }

  newNumberOfSharesForSubscriber(newNumberOfShares: number, id?: number) {
    return this.currentNumberOfSharesForSubscriber(id) + newNumberOfShares;
  }

  get remainingSharesToAllocate() {
    return Big(this.maxShares).minus(this.currentAllocatedShares).toNumber();
  }

  get currentNumberOfShares() {
    return (
      this.inputState?.numberOfShares.find((share) => share.shareTypeId === this.sharetypeId)
        ?.numberOfShares ?? 0
    );
  }

  get currentAllocatedShares() {
    return this.allocations?.reduce(
      (sum, allocation) => sum + allocation?.allocatedShares.toNumberOrZero(),
      0,
    );
  }

  get newTotalNumberOfShares() {
    return this.currentNumberOfShares + this.currentAllocatedShares;
  }

  fetchCurrentOwnership(id?: number) {
    const sharesForShareholder = this.currentNumberOfSharesForSubscriber(id);
    return this.currentNumberOfShares !== 0
      ? Big(sharesForShareholder).div(this.currentNumberOfShares).toNumber()
      : 0;
  }

  fetchNewOwnership(newNumberOfShares: number, id?: number) {
    const sharesForShareholder = this.newNumberOfSharesForSubscriber(newNumberOfShares, id);
    return this.newTotalNumberOfShares !== 0
      ? Big(sharesForShareholder).div(this.newTotalNumberOfShares).toNumber()
      : 0;
  }

  get totalPrice() {
    return this.allocations?.reduce(
      (sum, allocation) =>
        sum +
        Big(allocation.subscriptionPrice)
          .mul(allocation.allocatedShares.toNumberOrZero())
          .toNumber(),
      0,
    );
  }

  prepareData(): ShareAllocationRound {
    return {
      id: this.id,
      projectId: this.projectId,
      documentId: this.documentId,
      sharetypeId: this.sharetypeId,
      shareTypeCode: this.shareTypeCode,
      roundNumber: this.roundNumber,
      minShares: this.minShares,
      maxShares: this.maxShares,
      allocations: this.allocations.map((data) => data.prepareData()),
      isCompleted: this.isCompleted,
    };
  }

  get toTableData(): SubscriberAllocationRow[] {
    return this.allocations.map((subscriber) => {
      return {
        id: subscriber.subscriberId,
        title: subscriber.name,
        offeredShares: subscriber.offeredShares,
        subscribedShares: subscriber.subscribedShares,
        allocatedShares: subscriber.allocatedShares,
        subscriptionPrice: subscriber.subscriptionPrice,
        totalPrice: subscriber.totalPrice,
        currentOwnership: this.fetchCurrentOwnership(subscriber.shareholderId),
        newOwnership: this.fetchNewOwnership(
          subscriber.allocatedShares.toNumberOrZero(),
          subscriber.shareholderId,
        ),
        currentNumberOfShares: this.currentNumberOfSharesForSubscriber(subscriber.shareholderId),
        newNumberOfShares: this.newNumberOfSharesForSubscriber(
          subscriber.allocatedShares.toNumberOrZero(),
          subscriber.shareholderId,
        ),
      };
    });
  }
}

export class ShareAllocationModel {
  id: string;
  subscriberId: SubscriberId;
  shareholderId?: number;
  name: string;
  offeredShares: number;
  subscribedShares?: number;
  allocatedShares: NumberFieldModel;
  subscriptionPrice: number;

  constructor(data: ShareAllocation) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.id = data.id;
    this.subscriberId = data.subscriberId;
    this.shareholderId = data.shareholderId;
    this.name = data.name;
    this.offeredShares = data.offeredShares;
    this.subscribedShares = data.subscribedShares;
    this.allocatedShares = new NumberFieldModel({
      value: data.allocatedShares ?? data.subscribedShares,
      isPositiveOrZero: true,
      isInteger: true,
    });
    this.subscriptionPrice = data.subscriptionPrice;
  }

  get isAllocatedShares() {
    return !this.allocatedShares.isEmpty;
  }

  get totalPrice() {
    return Big(this.subscriptionPrice).mul(this.allocatedShares.toNumberOrZero()).toNumber();
  }

  prepareData(): ShareAllocation {
    return {
      id: this.id,
      subscriberId: this.subscriberId,
      shareholderId: this.shareholderId,
      name: this.name,
      offeredShares: this.offeredShares,
      subscribedShares: this.subscribedShares,
      allocatedShares: this.allocatedShares.toNumber(),
      subscriptionPrice: this.subscriptionPrice,
    };
  }
}

export class SearchCompanyModel {
  searchCompanies: (input: string) => void;
  selectedCompany?: SuggestedCompany;
  companies: SuggestedCompany[] = [];
  companyName: StringFieldModel;
  orgNo: StringFieldModel;

  constructor(
    searchCompanies: (input: string) => void,
    companyName: StringFieldModel,
    orgNo: StringFieldModel,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.searchCompanies = debounce((input: string) => searchCompanies(input), 250);
    this.companyName = companyName;
    this.orgNo = orgNo;
  }

  get disableOrgNo() {
    return Boolean(this.selectedCompany?.organizationnumber);
  }

  setCompanies(companies: SuggestedCompany[]) {
    this.companies = companies;
  }

  createCustomCompany(name: string): SuggestedCompany | undefined {
    return name !== "" ? { name: name, organizationnumber: "" } : undefined;
  }

  get suggestedCompanies() {
    const unchanged = this.selectedCompany?.name === this.companyName.inputValue;
    return compact([
      !unchanged ? this.createCustomCompany(this.companyName.inputValue) : undefined,
      ...this.companies,
    ]);
  }

  onSelectCompany(company: SuggestedCompany) {
    this.selectedCompany = company;
    this.companyName.setAndCommitInputValue(company.name);
    if (company.organizationnumber) {
      this.orgNo.setAndCommitInputValue(company.organizationnumber);
    } else {
      this.orgNo.clear();
    }
  }

  onClearCompany() {
    this.selectedCompany = undefined;
    this.companyName.clear();
    this.orgNo.clear();
    this.companies = [];
  }

  onInputValueChange(value: string) {
    if (this.selectedCompany) {
      this.orgNo.clear();
      this.selectedCompany = undefined;
    }
    this.companyName.setInputValue(value);
    this.searchCompanies(value);
  }
}

export type SubscriberDialogState =
  | { type: "none" }
  | {
      type: "deleteSubscriber";
      subscriberId: SubscriberId;
      submit: (value: SubscriberForIssueModel) => void;
    }
  | { type: "addSigners"; subscriberId: SubscriberId }
  | { type: "addContributionInKind"; subscriberId: SubscriberId; model: KindContributionModel }
  | { type: "addDebtContribution"; subscriberId: SubscriberId; model: DebtContributionModel };

export type SubscribersStatus =
  | SubscribersStatusForIssueModel
  | SubscribersStatusForNominalValueModel;

export class SubscribersStatusModel {
  readonly dispatch: DbotEventDispatcher;
  documentLanguages: Language[];
  subscribersModel: SubscribersStatus;
  signersForAllSubscribers: SubscriberSigner[] = [];
  addSignerForAllSubscribersModel?: { nameField: StringFieldModel; emailField: StringFieldModel };
  defaultExcessAmountMethod: ExcessAmountMethod;

  isLocked: boolean;

  constructor(
    dispatch: DbotEventDispatcher,
    readonly closeDialog: () => void,
    readonly orgNo: string,
    readonly initialState: Evaluation,
    readonly aiClient: AIClient,
    documentLanguages: Language[],
    isLocked: boolean,
    readonly inputState: InputState | undefined,
    defaultExcessAmountMethod: ExcessAmountMethod,
    data: IncreaseOfShareCapitalSubscribersStatus,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.isLocked = isLocked;
    this.documentLanguages = documentLanguages;
    this.defaultExcessAmountMethod = defaultExcessAmountMethod;
    this.subscribersModel = this.toModel(data);
  }

  handleEvent(event: DbotEvent) {
    if (this.subscribersModel?.type === IncreaseOfShareCapitalType.shareIssue)
      this.subscribersModel?.handleEvent(event);
  }

  setSubscribersModel(data?: IncreaseOfShareCapitalSubscribersStatus) {
    this.subscribersModel = this.toModel(data);
    this.signersForAllSubscribers = [];
  }

  addSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = {
      nameField: new StringFieldModel({ required: true, validator: validateName }),
      emailField: createEmailField({ required: true }),
    };
  }

  cancelSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = undefined;
  }

  submitSignerForAllSubscribers() {
    if (!this.addSignerForAllSubscribersModel) {
      return;
    }
    const { nameField, emailField } = this.addSignerForAllSubscribersModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.subscribersModel.setDirty();
      this.subscribersModel.subscribers.map((d) =>
        d.addSigner(nameField.validatedValue, emailField.validatedValue),
      );
      this.signersForAllSubscribers.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      this.addSignerForAllSubscribersModel = undefined;
    }
  }

  toModel(data?: IncreaseOfShareCapitalSubscribersStatus) {
    switch (data?.type) {
      case IncreaseOfShareCapitalType.increaseNominalValue:
        return new SubscribersStatusForNominalValueModel(
          this.aiClient,
          this.defaultExcessAmountMethod,
          this.dispatch,
          this.initialState,
          this.documentLanguages,
          this.inputState,
          data,
        );
      default:
        return new SubscribersStatusForIssueModel(
          this.aiClient,
          this.defaultExcessAmountMethod,
          this.dispatch,
          this.orgNo,
          this.initialState,
          this.documentLanguages,
          this.inputState,
          data,
        );
    }
  }

  saveAndClose() {
    this.subscribersModel.clearDirty();
    this.subscribersModel.save(this.signersForAllSubscribers);
    this.close();
  }

  close() {
    this.closeDialog();
  }
}

export class SubscribersStatusForNominalValueModel {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  contributionPerShareType?: ContributionPerShareType[];
  subscribers: SubscriberForIncreaseNominalValueModel[];
  selectedShareType?: NumberOfSharesForShareType;

  selectedSubscriberDialogState: SubscriberDialogState;
  defaultExcessAmountMethod: ExcessAmountMethod;

  isDirty = false;

  constructor(
    aiClient: AIClient,
    defaultExcessAmountMethod: ExcessAmountMethod,
    readonly dispatch: DbotEventDispatcher,
    readonly initialState: Evaluation,
    readonly documentLanguages: Language[],
    readonly inputState?: InputState,
    data?: IncreaseOfShareCapitalSubscribersStatusForNominalValue,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.type = IncreaseOfShareCapitalType.increaseNominalValue;
    this.contributionPerShareType = data?.contributionPerShareType;
    this.subscribers =
      data?.subscribers.map(
        (d) =>
          new SubscriberForIncreaseNominalValueModel(
            d,
            this.initialState,
            this.inputState,
            () => this.contributionPerShareType ?? [],
            aiClient,
            documentLanguages,
            defaultExcessAmountMethod,
          ),
      ) ?? [];
    this.selectedShareType = this.shareTypes.length > 0 ? this.shareTypes[0] : undefined;
    this.defaultExcessAmountMethod = defaultExcessAmountMethod;

    this.selectedSubscriberDialogState = noDialog();
  }

  get toTableData(): SubscriberStatusIssueRow[] {
    return [
      ...this.subscribers.map((subscriber) => {
        return {
          id: subscriber.subscriberid,
          title: subscriber.nameField.validatedValue,
          subscriptionPrice: this.contributionForSelectedShareType,
          currentShares:
            this.selectedShareType &&
            subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
          currentOwnership:
            this.selectedShareType &&
            percentOf(
              subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
              this.currentTotalSharesForShareType,
            ),
          totalPrice: this.getTotalContributionForSubscriber(subscriber),
          subscriber: subscriber,
        };
      }),
      {
        id: "total",
        subscriptionPrice: this.contributionForSelectedShareType,
        currentShares: this.sumOfCurrentSharesForSubscribers,
        totalPrice: this.totalContribution,
      },
    ];
  }

  save(signersForAllSubscribers: SubscriberSigner[]) {
    this.dispatch({
      type: "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue",
      subscribers: this.subscribers.map((d) => toSubscriberForIncreaseNominalValue(d)),
      signersForAllSubscribers,
    });
  }

  get shareTypes() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.initialState.shareTypes,
      this.inputState?.numberOfShares,
    ).filter((d) => d.shareTypeCode !== undefined && d.numberOfShares > 0);
  }

  setSelectedShareType(shareType: NumberOfSharesForShareType) {
    this.selectedShareType = shareType;
  }

  get currentTotalSharesForShareType(): number {
    return this.selectedShareType ? this.selectedShareType.numberOfShares : 0;
  }

  get contributionForSelectedShareType() {
    return (
      this.contributionPerShareType?.find(
        (d) => d.sharetypeId === this.selectedShareType?.shareTypeId,
      )?.contributionPerShare ?? 0
    );
  }

  getTotalContributionForSubscriber(subscriber: SubscriberForIncreaseNominalValueModel) {
    return this.selectedShareType
      ? Big(this.contributionForSelectedShareType)
          .times(
            subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
          )
          .toNumber()
      : 0;
  }

  get totalContribution() {
    return this.subscribers
      .reduce(
        (sum, subscriber) => sum.plus(this.getTotalContributionForSubscriber(subscriber)),
        Big(0),
      )
      .toNumber();
  }

  get sumOfCurrentSharesForSubscribers() {
    return this.subscribers
      .reduce(
        (sum, subscriber) =>
          sum.plus(
            this.selectedShareType
              ? subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId)
              : 0,
          ),
        Big(0),
      )
      .toNumber();
  }

  get selectedSubscriber() {
    switch (this.selectedSubscriberDialogState.type) {
      case "addSigners": {
        const subscriberId = this.selectedSubscriberDialogState.subscriberId;
        return this.subscribers.find((d) => d.subscriberid === subscriberId);
      }
      default:
        return undefined;
    }
  }

  showSubscriberSignerDialog(subscriber: SubscriberForCapitalIncrease) {
    this.selectedSubscriberDialogState = {
      type: "addSigners",
      subscriberId: subscriber.subscriberid,
    };
  }

  closeSelectedSubscriberDialog() {
    this.selectedSubscriber?.refreshSignerChoice();
    this.selectedSubscriberDialogState = noDialog();
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }
}

export class SubscribersStatusForIssueModel {
  type: IncreaseOfShareCapitalType.shareIssue;
  shareTypes: ShareTypeForSubscribersStatusModel[];
  subscribers: SubscriberForIssueModel[];
  isPreferentialRights: boolean;
  isOverSubscriptionAllowed: boolean;
  selectedShareType?: ShareTypeForSubscribersStatusModel;

  isAddNewShareholderDialog: boolean = false;
  newSubscriberForIssue: SubscriberForIssueModel;
  selectedSubscriberDialogState: SubscriberDialogState;
  searchCompany: SearchCompanyModel;
  addShareholdersFromActorsModel?: ActorsSelectorModel;

  defaultExcessAmountMethod: ExcessAmountMethod;

  isDirty = false;

  constructor(
    readonly aiClient: AIClient,
    defaultExcessAmountMethod: ExcessAmountMethod,
    readonly dispatch: DbotEventDispatcher,
    readonly orgNo: string,
    readonly initialState: Evaluation,
    readonly documentLanguages: Language[],
    readonly inputState?: InputState,
    readonly data?: IncreaseOfShareCapitalSubscribersStatusForIssue,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.type = IncreaseOfShareCapitalType.shareIssue;
    this.subscribers =
      data?.subscribers.map(
        (d) =>
          new SubscriberForIssueModel(
            d,
            this.initialState,
            this.inputState,
            this.getSelectedShareTypeIds,
            aiClient,
            documentLanguages,
            defaultExcessAmountMethod,
            () => this.inputState?.sharenominalValue ?? 0,
          ),
      ) ?? [];
    this.isPreferentialRights = data?.isPreferentialRights ?? false;
    this.isOverSubscriptionAllowed = data?.isOverSubscriptionAllowed ?? false;
    this.shareTypes =
      data?.selectedShareTypes.map(
        (shareType) =>
          new ShareTypeForSubscribersStatusModel(
            shareType,
            initialState.shareTypes.find((s) => s.sharetypeId === shareType.sharetypeId)!,
          ),
      ) ?? [];
    this.selectedShareType = this.shareTypes.length > 0 ? this.shareTypes[0] : undefined;

    this.newSubscriberForIssue = this.createSubscriber();
    this.searchCompany = new SearchCompanyModel(
      this.searchCompanies,
      this.newSubscriberForIssue.nameField,
      this.newSubscriberForIssue.organisationNumber,
    );
    this.selectedSubscriberDialogState = noDialog();
    this.defaultExcessAmountMethod = defaultExcessAmountMethod;
  }

  getSelectedShareTypeIds(): number[] {
    return this.data?.selectedShareTypes.map((x) => x.sharetypeId) ?? [];
  }

  get existingShareholders() {
    return toExistingShareholders(this.initialState.shareholders);
  }

  save(signersForAllSubscribers: SubscriberSigner[]) {
    this.dispatch({
      type: "dbot/addIncreaseOfShareCapitalSubscribersForIssue",
      subscribers: this.subscribers.map((d) => toSubscriberForIssue(d)),
      signersForAllSubscribers,
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.searchCompany.setCompanies(event.result);
        }
        break;
    }
  }

  searchCompanies(input: string) {
    this.dispatch({ type: "dbot/search-companies", input, source: this });
  }

  get isSubscribersEmpty() {
    return this.subscribers.length === 0;
  }

  setSelectedShareType(value: ShareTypeForSubscribersStatusModel) {
    this.selectedShareType = value;
  }

  get subscribedShareSumType() {
    return this.shareTypes.reduce((map: Record<string, number>, shareType) => {
      map[shareType.sharetypeCode] = this.totalSubscribedSharesForShareType(shareType.sharetypeId);
      return map;
    }, {});
  }

  get sumOfCurrentSharesForSubscribers() {
    return this.subscribers
      .reduce(
        (sum, subscriber) =>
          sum.plus(
            this.selectedShareType
              ? subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.sharetypeId)
              : 0,
          ),
        Big(0),
      )
      .toNumber();
  }

  get sumOfNewSharesForSubscribers() {
    return this.sumOfCurrentSharesForSubscribers + this.totalSubscribedShares;
  }

  getShareSumByType(shareTypeId: number) {
    return (
      this.inputState?.numberOfShares.find((d) => d.shareTypeId === shareTypeId)?.numberOfShares ??
      0
    );
  }

  get currentTotalSharesForShareType(): number {
    return this.selectedShareType ? this.getShareSumByType(this.selectedShareType.sharetypeId) : 0;
  }

  get newTotalSharesForShareType() {
    return this.currentTotalSharesForShareType + this.totalSubscribedShares;
  }

  get remainingSharesToSubscribe() {
    return this.selectedShareType
      ? Big(this.selectedShareType.maxNumberOfShares).minus(this.totalSubscribedShares).toNumber()
      : 0;
  }

  totalSubscribedSharesForShareType(shareTypeId: number): number {
    return this.subscribers
      .reduce(
        (sum, subscriber) => sum.plus(subscriber.getNumberOfSharesForShareType(shareTypeId)),
        Big(0),
      )
      .toNumber();
  }

  get totalSubscribedShares() {
    return this.selectedShareType
      ? this.totalSubscribedSharesForShareType(this.selectedShareType.sharetypeId)
      : 0;
  }

  get totalSubscribedPrice() {
    return this.selectedShareType
      ? this.subscribers
          .reduce(
            (sum, subscriber) =>
              sum.plus(
                this.selectedShareType
                  ? subscriber.getTotalPriceForShareType(this.selectedShareType.sharetypeCode)
                  : 0,
              ),
            Big(0),
          )
          .toNumber()
      : 0;
  }

  get selectedSubscriber() {
    switch (this.selectedSubscriberDialogState.type) {
      case "none":
        return undefined;
      case "deleteSubscriber":
      case "addSigners": {
        const subscriberId = this.selectedSubscriberDialogState.subscriberId;
        return this.subscribers.find((d) => d.subscriberid === subscriberId);
      }
    }
  }

  showSubscriberSignerDialog(subscriber: SubscriberForIssueModel) {
    this.selectedSubscriberDialogState = {
      type: "addSigners",
      subscriberId: subscriber.subscriberid,
    };
  }

  deleteSubscriber(subscriber: SubscriberForIssueModel) {
    if (subscriber.hasShareSubscriptions) {
      this.selectedSubscriberDialogState = {
        type: "deleteSubscriber",
        subscriberId: subscriber.subscriberid,
        submit: this.deleteSubscriberRow,
      };
    } else {
      this.deleteSubscriberRow(subscriber);
    }
  }

  closeSelectedSubscriberDialog() {
    this.selectedSubscriber?.refreshSignerChoice();
    this.selectedSubscriberDialogState = noDialog();
  }

  deleteSubscriberRow(subscriber: SubscriberForIssueModel) {
    this.setDirty();
    this.subscribers = this.subscribers.filter((value) => value !== subscriber);
    this.closeSelectedSubscriberDialog();
  }

  openAddNewShareholderFromPortfolioDialog() {
    this.addShareholdersFromActorsModel = createAddShareholdersFromActorsModel(
      this.orgNo,
      this.dispatch,
      this.subscribers,
      this.existingShareholders,
      this.addSubscribers,
      this.closeAddShareholderFromActorsDialog,
    );
  }

  closeAddShareholderFromActorsDialog() {
    this.addShareholdersFromActorsModel = undefined;
  }

  addSubscribers(newSubscribers: SubscriberForIssue[], shareholders?: ShareholderDTO[]) {
    const selectedSubscribers = newSubscribers.map((s) => this.createSubscriber(s));
    this.subscribers.push(...selectedSubscribers);
    if (shareholders !== undefined) {
      this.initialState.shareholders.push(...shareholders);
    }
    this.setDirty();
  }

  openAddNewShareholderDialog(params: { isCompany: boolean }) {
    this.newSubscriberForIssue = this.createSubscriber(params);
    this.searchCompany = new SearchCompanyModel(
      this.searchCompanies,
      this.newSubscriberForIssue.nameField,
      this.newSubscriberForIssue.organisationNumber,
    );
    this.isAddNewShareholderDialog = true;
  }

  submitNewShareholderSubscriber() {
    if (this.newSubscriberForIssue.validate()) {
      this.newSubscriberForIssue.addSubscriberAsSigner();
      this.subscribers = this.subscribers.concat(this.newSubscriberForIssue);
      this.setDirty();
      this.closeAddSubscriberDialog();
    }
  }

  closeAddSubscriberDialog() {
    this.isAddNewShareholderDialog = false;
  }

  private createSubscriber(data?: Partial<SubscriberForIssue>) {
    const subscriber = createSubscriberForIssue({
      ...data,
      subscriptions: this.shareTypes.map<Subscription>((s) => ({
        id: newUuid(),
        sharetypeId: s.sharetypeId,
        shareTypeCode: s.sharetypeCode,
        numberOfShares: 0,
        subscriptionPrice: s.sharePrice,
      })),
    });
    return new SubscriberForIssueModel(
      subscriber,
      this.initialState,
      this.inputState,
      this.getSelectedShareTypeIds,
      this.aiClient,
      this.documentLanguages,
      this.defaultExcessAmountMethod,
      () => this.inputState?.sharenominalValue ?? 0,
    );
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }
}

export class ShareTypeForSubscribersStatusModel {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  fixed?: Fixed;
  interval?: Interval;
  issueMode: ShareIssueMode;
  sharePrice: number;
  isIndividualSharePrice: boolean;

  constructor(
    public data: ShareIssuePerShareType,
    readonly shareType: ShareType,
  ) {
    this.sharetypeId = data.sharetypeId;
    this.sharetypeCode = data.sharetypeCode;
    this.fixed = asFixed(data.issueMode);
    this.interval = asInterval(data.issueMode);
    this.issueMode = data.issueMode;
    this.sharePrice = data.sharePrice ?? 0;
    this.isIndividualSharePrice = data.isIndividualSharePrice ?? false;
    makeAutoObservable(this);
  }

  get isFixed() {
    return this.issueMode.type === "fixed";
  }

  get minNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares ?? 0;
      case "interval":
        return this.issueMode.from ?? 0;
    }
  }

  get maxNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares ?? 0;
      case "interval":
        return this.issueMode.to ?? 0;
    }
  }
}

export function toProperties(
  model: ShareCapitalIncreaseTypeModel,
  contributionTypes: ContributionType[],
): ShareCapitalIncreaseType {
  if (model.type === "shareIssue") {
    return toShareIssue(model, contributionTypes);
  } else {
    return toIncreaseNominalValue(model, contributionTypes);
  }
}

function toIncreaseNominalValue(
  model: IncreaseNominalValueModel,
  contributionTypes: ContributionType[],
): IncreaseNominalValue {
  return {
    type: model.type,
    increaseInNominalValue: model.increaseInNominalValue.toNumber(),
    sharePrice: model.sharePrice.toNumber(),
    contributionPerShareTypes: model.contributionPerShareTypes.map((s) => s.toModel()),
    subscribers: model.subscribers.map((s) =>
      toSubscriberForIncreaseNominalValue(s, contributionTypes),
    ),
  };
}

function toShareIssueForShareTypes(model: ShareIssueModel): ShareIssuePerShareType[] {
  return model.selectedShareTypes.selected.map((s) => {
    return {
      sharetypeId: s.sharetypeId,
      sharetypeCode: s.sharetypeCode,
      isNewShareType: s.isNewShareType,
      issueMode: toShareIssueMode(s.model.issueMode),
      sharePrice: s.model.sharePrice.toNumber(),
      isIndividualSharePrice: s.model.isIndividualSharePrice.value ?? false,
    };
  });
}

function toShareIssue(model: ShareIssueModel, contributionTypes: ContributionType[]): ShareIssue {
  return {
    type: model.type,
    nominalValue: model.nominalValue.toNumber(),
    preferentialRight: model.preferentialRight.value,
    preferentialRightTransferable: model.preferentialRightTransferable.value,
    preferentialRightOversubscribe: model.preferentialRightOversubscribe.value,
    preferentialBoardRightsToSellSubscription:
      model.preferentialBoardRightsToSellSubscription.value,
    externalSubscribers: model.externalSubscribers.value,
    preferentialRightWaivedReason: model.preferentialRightWaivedReasonModel.value,
    preferentialRightWaivedReasonTexts: model.preferentialRightWaivedReasonTexts.map((t) => ({
      language: t.locale,
      text: t.model.validatedValue,
    })),
    shareholderRights: model.shareholderRights.value,
    isGuaranteeConsortiumEstablished: model.isGuaranteeConsortiumEstablished.value,
    guaranteeConsortiumCommission: toGuaranteeConsortiumCommission(
      model.guaranteeConsortiumCommission,
    ),
    shareIssuePerShareTypes: toShareIssueForShareTypes(model),
    subscribers: model.subscribers.map((s) => toSubscriberForIssue(s, contributionTypes)),
    preferentialIssueSubscribers: model.preferentialIssueSubscribers.map((s) =>
      toSubscriberForIssue(s, contributionTypes),
    ),
  };
}

function toSubscriberForIssue(
  s: SubscriberForIssueModel,
  contributionTypes: ContributionType[] = Object.values(ContributionType),
): SubscriberForIssue {
  return {
    ...s.data,
    name: s.isExisting ? undefined : s.nameField.validatedValue,
    email: s.isExisting ? undefined : s.email.validatedValue,
    address: s.address.validatedValue,
    nationality: s.isExisting ? undefined : s.nationality.validatedValue,
    organisationNumber:
      !s.isExisting && s.isCompany ? s.organisationNumber.validatedValue : undefined,
    contactName: !s.isExisting && s.isCompany ? s.contactName.validatedValue : undefined,
    debtContribution: contributionTypes.includes(ContributionType.debt)
      ? s.debtContributionModel.toDebtContribution()
      : undefined,
    kindContribution: contributionTypes.includes(ContributionType.kind)
      ? s.kindContributionModel.toKindContribution()
      : undefined,
    subscriptions: s.subscriptions.map((d) => toSubscriptionForIssue(d)),
    isSigningSelf: s.isSigningSelf,
    signers: s.signers.map((d) => d.toSubscriberSigner()),
    isCompany: s.isCompany,
    nationalId: s.isExisting ? undefined : s.nationalId.validatedValue,
    personReference: s.isExisting ? undefined : s.data.personReference,
  };
}

function toSubscriptionForIssue(s: SubscriptionForIssueModel): Subscription {
  return {
    id: s.id,
    sharetypeId: s.sharetypeId,
    shareTypeCode: s.shareTypeCode,
    numberOfShares: s.numberOfShares.toNumberOrZero(),
    subscriptionPrice: s.subscriptionPrice.toNumberOrZero(),
  };
}

function toSubscriberForIncreaseNominalValue(
  s: SubscriberForIncreaseNominalValueModel,
  contributionTypes: ContributionType[] = Object.values(ContributionType),
): SubscriberForIncreaseNominalValue {
  return {
    ...s.data,
    debtContribution: contributionTypes.includes(ContributionType.debt)
      ? s.debtContributionModel.toDebtContribution()
      : undefined,
    kindContribution: contributionTypes.includes(ContributionType.kind)
      ? s.kindContributionModel.toKindContribution()
      : undefined,
    isSigningSelf: s.isSigningSelf,
    signers: s.signers.map((d) => d.toSubscriberSigner()),
  };
}

function toShareIssueMode(model: ShareIssueModeModel): ShareIssueMode {
  if (model.type === "interval") {
    return toShareIssueInterval(model);
  } else {
    return toShareIssueFixed(model);
  }
}

function toShareIssueFixed(model: FixedModel): Fixed {
  return {
    type: model.type,
    numberOfShares: model.numberOfShares.toNumber(),
    calculateFromSubscriptions: model.calculateFromSubscriptions.value,
  };
}

function toShareIssueInterval(model: IntervalModel): Interval {
  return {
    type: model.type,
    from: model.from.toNumber(),
    to: model.to.toNumber(),
  };
}

export function toPeriodForSubscription(model: PeriodForSubscriptionModel): PeriodForSubscription {
  return {
    from: model.from.toISOString(),
    to: model.to.toISOString(),
  };
}

function toGuaranteeConsortiumCommission(
  model: GuaranteeConsortiumCommissionModel,
): GuaranteeConsortiumCommission {
  return {
    numberOfShares: model.numberOfShares.toNumber(),
    percentOfGuaranteedAmount: model.percentOfGuaranteedAmount.toNumber(),
  };
}

export type ProRataAllocation = {
  shareholder: ShareholderDTO;
  shareType: ShareTypeOption;
  numberOfSharesToIssue: number;
  fractionShare: number;
};

function getAddressAsString(address?: Address) {
  return compact([
    address?.address,
    address?.postalCode || address?.city
      ? compact([address?.postalCode, address?.city]).join(" ")
      : undefined,
    getAllCountriesSortedByName(Language.NO).find((c) => c.code === address?.country)?.name,
  ])
    .join(", ")
    .trim();
}

export function subscribersForIssueFromProRataCalculation(
  data: SubscriberForIssue[],
  shareTypes: ShareTypeOption[],
  totalExistingShares: number,
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): SubscriberForIssue[] {
  const allocations = shareTypes.flatMap((shareType) =>
    proRataCalculation(shareType, totalExistingShares, shareholders, sharesForShareholders),
  );
  const uniqueShareholders = Array.from(new Set(allocations.map((d) => d.shareholder)));
  return uniqueShareholders.map((shareholder) => {
    const currentData = data.find((d) => d.shareholderId === shareholder.id);
    return {
      id: shareholder.id,
      subscriberid: currentData?.subscriberid ?? newUuid(),
      shareholderId: shareholder.id,
      name: shareholder.name,
      email: currentData?.email ?? shareholder.email,
      address: currentData?.address,
      organisationNumber: shareholder.isCompany ? shareholder.organizationNumber : undefined,
      isExisting: true,
      isCompany: shareholder.isCompany,
      contactName:
        currentData?.contactName ??
        (shareholder.isCompany ? getFullName(shareholder.contactInfo) : undefined),
      debtContribution: currentData?.debtContribution,
      kindContribution: currentData?.kindContribution,
      subscriptions: allocations
        .filter((d) => d.shareholder.id === shareholder.id)
        .map((allocation) => {
          return {
            id: newUuid(),
            sharetypeId: allocation.shareType.sharetypeId,
            shareTypeCode: allocation.shareType.sharetypeCode,
            numberOfShares: allocation.numberOfSharesToIssue,
            subscriptionPrice: allocation.shareType.model.sharePrice.toNumberOrZero(),
          };
        }),
      isSigningSelf: currentData?.isSigningSelf ?? true,
      signers: currentData?.signers ?? [],
    };
  });
}

export function proRataCalculation(
  shareType: ShareTypeOption,
  totalExistingShares: number,
  shareholders: ShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): ProRataAllocation[] {
  if (totalExistingShares <= 0) {
    return [];
  }
  const totalIssuedPerShareType = shareType.model.maxNumberOfShares;
  const activeShareholders = shareholders.filter((s) =>
    sharesForShareholders.some((share) => share.shareholderId === s.id && share.numberOfShares > 0),
  );
  const allocatedShares: ProRataAllocation[] = activeShareholders.map((shareholder) => {
    const numberOfSharesForShareholder = sharesForShareholders
      .filter((share) => share.shareholderId === shareholder.id)
      .reduce((sum, share) => sum + share.numberOfShares, 0);
    const ownership = Big(numberOfSharesForShareholder).div(totalExistingShares);
    const allocated = Big(totalIssuedPerShareType).mul(ownership);
    const sharesWithoutFraction = allocated.round(0, Big.roundDown);
    return {
      shareholder: shareholder,
      shareType: shareType,
      numberOfSharesToIssue: sharesWithoutFraction.toNumber(),
      fractionShare: allocated.minus(sharesWithoutFraction).toNumber(),
    };
  });
  const remainingShares =
    totalIssuedPerShareType -
    allocatedShares.reduce((sum, value) => sum + value.numberOfSharesToIssue, 0);
  return addRemainingSharesByHighestFractionForProRataCalculation(allocatedShares, remainingShares);
}

export function addRemainingSharesByHighestFractionForProRataCalculation(
  allocation: ProRataAllocation[],
  remainingShares: number,
): ProRataAllocation[] {
  return allocation
    .sort((a, b) => b.fractionShare - a.fractionShare)
    .map((allocation, index) => {
      return {
        shareholder: allocation.shareholder,
        shareType: allocation.shareType,
        numberOfSharesToIssue:
          index < remainingShares
            ? allocation.numberOfSharesToIssue + 1
            : allocation.numberOfSharesToIssue,
        fractionShare: 0,
      };
    });
}

export interface SubscriberStatusIssueRow {
  id: string;
  title?: string;
  numberOfShares?: number;
  subscriptionPrice?: number;
  totalPrice?: number;
  currentShares?: number;
  currentOwnership?: number;
  newShares?: number;
  newOwnership?: number;
  subscriber?: SubscriberForCapitalIncrease;
}

export interface SubscriberAllocationRow {
  id: string;
  title?: ReactNode;
  offeredShares?: number;
  subscribedShares?: number;
  allocatedShares: NumberFieldModel;
  subscriptionPrice?: number;
  totalPrice: number;
  currentOwnership: number;
  newOwnership: number;
  currentNumberOfShares: number;
  newNumberOfShares: number;
}

export function getExistingSubscribersForIncreaseNominalValue(
  shareholders: ShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  return data.filter((sub) => shareholders.some((s) => sub.shareholderId === s.id));
}

export function getNewShareholderSubscribersForIncreaseNominalValue(
  shareholders: ShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  return shareholders
    .filter((s) => !data?.some((sub) => sub.shareholderId === s.id))
    .map((s) => ({
      subscriberid: newUuid(),
      shareholderId: s.id,
      name: s.name,
      isSigningSelf: true,
      signers: [],
    }));
}

export function getSubscribersForIncreaseNominalValue(
  shareholders: ShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  //we gotta convert all active shareholders to the model, cause for the increase of nominal value all existing shareholders
  //should participate
  const shareholderSubscribers = getNewShareholderSubscribersForIncreaseNominalValue(
    shareholders,
    data,
  );
  const existingSubscribers = getExistingSubscribersForIncreaseNominalValue(shareholders, data);
  return existingSubscribers.concat(shareholderSubscribers);
}

export function existingShareholderText(n: number): TMessage | undefined {
  return n > 1
    ? { key: "subscribers.valid.existingShareholder.plural", params: { n: n } }
    : n === 1
      ? { key: "subscribers.valid.existingShareholder.singular", params: { n: n } }
      : undefined;
}

export function newShareholderText(n: number): TMessage | undefined {
  return n > 1
    ? { key: "subscribers.valid.newShareholder.plural", params: { n: n } }
    : n === 1
      ? { key: "subscribers.valid.newShareholder.singular", params: { n: n } }
      : undefined;
}

export function formatValidSubscribersLabel(
  activeSubscribersForIssue: SubscriberForIssueModel[],
): TMessage {
  const existing = activeSubscribersForIssue.filter(
    (d) => d.isExisting && d.hasShareSubscriptions,
  ).length;
  const others = activeSubscribersForIssue.filter(
    (d) => !d.isExisting && d.hasShareSubscriptions,
  ).length;
  const texts = compact([existingShareholderText(existing), newShareholderText(others)]);
  return {
    key: "subscribers.valid",
    params: { shareholders: texts },
  };
}

export function subscriberSignerLabel(subscriber: SubscriberForCapitalIncrease): TMessage {
  return subscriber.isCompany && subscriber.isSignerChoiceSelf
    ? { key: "add" }
    : !subscriber.isCompany && subscriber.isSignerChoiceSelf
      ? { key: "decisions.increaseShareCapital.subscribersStatus.signers.self" }
      : { translated: subscriber.signers.length.toString() };
}

function withoutSpaces(value: string | undefined): string | undefined {
  return value?.replace(/\s/g, "");
}

function isActorExistingShareholder(actor: SelectedActor, shareholder: ExistingShareholder) {
  return (
    actor.reference === toShareholderReference(shareholder.id) ||
    actor.inCompany?.shareholderId === shareholder.id ||
    (!!actor.companyNumber && actor.companyNumber === shareholder.companyNumber)
  );
}

function createSubscriberForIssue(data: Partial<SubscriberForIssue>): SubscriberForIssue {
  return {
    ...data,
    subscriptions: data.subscriptions ?? [],
    signers: [],
    isSigningSelf: data.isSigningSelf ?? true,
    id: data?.id ?? -new Date().valueOf(),
    subscriberid: newUuid(),
    shareholderId: data?.shareholderId,
    name: data?.name ?? "",
    email: data?.email,
    isExisting: data?.isExisting ?? false,
    isCompany: data?.isCompany ?? false,
    contactName: data?.contactName,
  };
}

export function mapExistingShareholdersToSubscriberForIssue(
  shareholders: ExistingShareholder[],
): SubscriberForIssue[] {
  return shareholders.map(({ isCompany, id, name, email, contactName }) =>
    createSubscriberForIssue({
      isCompany,
      id: id,
      shareholderId: id,
      name,
      email,
      isExisting: true,
      contactName,
    }),
  );
}

export function mapShareholderDTOToSubscriberForIssue(
  shareholders: ShareholderDTO[],
  initialData: (SubscriberForIssue | undefined)[] = [],
): SubscriberForIssue[] {
  return shareholders.map(({ isCompany, id, name, email, contactInfo }, i) =>
    createSubscriberForIssue({
      ...initialData[i],
      isCompany,
      id: id,
      shareholderId: id,
      name,
      email,
      isExisting: true,
      contactName: isCompany ? getFullName(contactInfo) : undefined,
    }),
  );
}

export function mapActorsToSubscriberForIssue(actors: SelectedActor[]): SubscriberForIssue[] {
  return actors.map((actor) => {
    const ref = resolveUrnReference(actor.reference);
    return createSubscriberForIssue({
      shareholderId: ref?.type === "shareholderId" ? ref.id : undefined,
      isCompany: actor.isCompany,
      organisationNumber: actor.companyNumber,
      nationalId: actor.socialSecurityNumber,
      personReference: actor.isCompany ? undefined : actor.reference,
      name: actor.name,
      contactName: actor.representativeName,
      email: actor.email,
      nationality: actor.nationality,
    });
  });
}

function toExistingShareholders(shareholders: ShareholderDTO[]): ExistingShareholder[] {
  return shareholders.map((s) => ({
    id: s.id,
    name: s.name,
    email: s.email,
    isCompany: s.isCompany,
    isSelected: false,
    contactName: s.isCompany ? getFullName(s.contactInfo) : undefined,
    companyNumber: s.companyNumber,
  }));
}

export function createAddShareholdersFromActorsModel(
  orgNo: string,
  dispatch: DbotEventDispatcher,
  subscribers: SubscriberForIssueModel[],
  shareholders: ExistingShareholder[],
  addSubscribers: (newSubscribers: SubscriberForIssue[], shareholders?: ShareholderDTO[]) => void,
  onClose: () => void,
): ActorsSelectorModel {
  return new ActorsSelectorModel({
    title: { key: "shareholders.add-shareholders" },
    organisationNumber: orgNo,
    tabs: shareRecipientActorTabs,
    excludeActors: subscribers.flatMap((subscriber) => subscriber.excludedActorIds),
    onLoad: (type, onSuccess, onFailure) => loadActors(type, onSuccess, onFailure, dispatch, orgNo),
    onSubmit: (selectedActors) => {
      const [rest, newSubscribers] = partition(selectedActors, (actor) =>
        shareholders.some((s) => isActorExistingShareholder(actor, s)),
      );
      const existingShareholders = rest.map((actor) =>
        shareholders.find((s) => isActorExistingShareholder(actor, s))!,
      );
      addSubscribers([
        ...mapExistingShareholdersToSubscriberForIssue(Array.from(new Set(existingShareholders))),
        ...mapActorsToSubscriberForIssue(newSubscribers),
      ]);
      onClose();
    },
    onClose,
    isMultiSelect: true,
  });
}

function quickSetupSuccessMessage(type?: QuickSetupType): TMessage {
  return type === undefined
    ? { key: "decisions.increaseShareCapital.quickSetupSelector.removed.success" }
    : { key: "decisions.increaseShareCapital.quickSetupSelector.added.success" };
}

export function filterSubscribersOnSelectedShareTypes(
  subscribers: SubscriberForIssue[],
  selectedShareTypes: ShareIssuePerShareType[],
): SubscriberForIssue[] {
  return subscribers
    .map((subscriber) => ({
      ...subscriber,
      subscriptions: subscriber.subscriptions.filter((subscription) =>
        selectedShareTypes.some((shareType) => shareType.sharetypeId === subscription.sharetypeId),
      ),
    }))
    .filter(
      (subscriber) =>
        subscriber.subscriptions.length > 0 &&
        subscriber.subscriptions.some((a) => a.numberOfShares > 0),
    );
}

export function toIncreaseOfShareCapitalSubscribersStatus(
  data: IncreaseOfShareCapital,
): IncreaseOfShareCapitalSubscribersStatus {
  const properties = data.properties;
  switch (properties.type) {
    case IncreaseOfShareCapitalType.shareIssue: {
      const isPreferentialRights =
        data.contributionTypes.includes(ContributionType.cash) &&
        properties.preferentialRight === true;
      return {
        type: IncreaseOfShareCapitalType.shareIssue,
        selectedShareTypes: properties.shareIssuePerShareTypes,
        subscribers: isPreferentialRights
          ? properties.preferentialIssueSubscribers
          : properties.subscribers,
        isPreferentialRights: isPreferentialRights,
        isOverSubscriptionAllowed: properties.preferentialRightOversubscribe === true,
        subscriptionPeriod: { ...data.periodForSubscription },
      };
    }
    case IncreaseOfShareCapitalType.increaseNominalValue:
      return {
        type: IncreaseOfShareCapitalType.increaseNominalValue,
        contributionPerShareType: properties.contributionPerShareTypes,
        subscribers: properties.subscribers ?? [],
      };
  }
}

export function findIncreaseOfShareCaptial(
  context: ProjectContext,
): IncreaseOfShareCapital | undefined {
  return first(
    context.currentDecisions.flatMap((d) =>
      d.decisionData?.type === DecisionType.increaseOfShareCapital ? [d.decisionData] : [],
    ),
  );
}

export const prepAddSubscribersDropdown = ({
  form,
  variant = "link",
  alignment = "start",
}: {
  form: IncreaseOfShareCapitalForm;
  variant?: "primary" | "secondary" | "link" | "mini-primary";
  alignment?: DropdownMenuProps["alignment"];
}): DropdownMenuProps => {
  const disabled = form.isProjectLocked || form.isPreferentialRight;
  const label: TMessage = {
    key: "decisions.increaseShareCapital.subscribersStatus.addSubscribers",
  };
  return {
    alignment,
    button:
      variant === "link"
        ? { type: "link", icon: "plus", label, disabled }
        : variant === "secondary"
          ? { type: "button", variant: "secondary", icon: "plus", label, disabled }
          : variant === "primary"
            ? { type: "button", variant: "primary", icon: "plus", label, disabled }
            : {
                type: "button",
                variant: "primary",
                icon: "plus",
                tooltip: label,
                size: "xs",
                disabled,
              },
    items: [
      {
        type: "item",
        label: { key: "subscriber.addBuyer.existing" },
        onSelect: () => form.shareIssue.openAddNewShareholderFromPortfolioDialog(),
      },
      {
        type: "item",
        label: { key: "subscriber.addBuyer.newPersonalSubscriber" },
        onSelect: () => form.shareIssue.addNewSubscriber(false),
      },
      {
        type: "item",
        label: { key: "subscriber.addBuyer.newCompanySubscriber" },
        onSelect: () => form.shareIssue.addNewSubscriber(true),
      },
    ],
  };
};
